/* eslint-disable react-hooks/exhaustive-deps */
import React from "react";
import { checkPermission, usePermissions } from "../../components/PermissionsContext";
import {
  AccessTimeRounded,
  AcUnitRounded,
  CheckCircleOutlineRounded,
  EmojiEventsRounded,
  EqualizerRounded,
  ErrorOutlineRounded,
  ExpandMoreRounded,
  GroupsRounded,
  HistoryRounded,
  HistoryToggleOffRounded,
  InfoRounded,
  LocalFireDepartmentRounded,
  LockRounded,
  OutlinedFlagRounded,
  PauseCircleFilled,
  ReplyRounded,
  SpeedOutlined,
  StarRounded,
  TimerRounded,
  TrendingDownRounded,
  TrendingUpRounded,
  VisibilityOffRounded,
  WarningAmberRounded,
} from "@mui/icons-material";
import { Chip } from "../../components/Chip";
import { negotiateAPI, onRTAReconnect, RTA, sessionInfo } from "../..";
import { CircularProgress } from "@mui/material";
import { NegotiationConfig } from "./negotiate.config";
import { NegotiationNotices } from "../../components/NegotiationBalance/NegotiationNotices";
import { useUser } from "../../components/UserContext";
import { useNegotiationNotices } from "../../components/NegotiationBalance/useNegotiationNotices";
import { termosEnterpriseNegotiationBalanceVersion } from "./legal/content/termosEnterprise";
import KeyboardDoubleArrowUpRoundedIcon from "@mui/icons-material/KeyboardDoubleArrowUpRounded";
import localforage from "localforage";
import { getBandwidthMbps } from "../../utils/utils";
import {
  type HallOfCardsSnapshot,
  type HallPostsWire,
  MARKET_PERIOD_POLL_MS,
  buildHighlightPositionTable,
  mapHallOfCards,
  marketCountdown,
  parseMarketEndAt,
  resolveHighlightClassName,
  shouldFetchHallCards,
} from "./negotiate.utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCog } from "@fortawesome/free-solid-svg-icons";
import { FloatContainer } from "../../components/FloatContainer";
import {
  bidCandidate,
  displayedCardBid,
  type HallCardWire,
  isCurrentBid,
  remainingBidCooldown,
  withUnbidDefaults,
} from "./negotiate.bidState";
import { BannerFeedback } from "../../components/BannerFeedback";
import { getUserFacingError } from "../../utils/userFacingError";
import {
  getNegotiationModeRevision,
  getNegotiationModeError,
  isMockNegotiation,
  isNegotiationModeReady,
  onNegotiationModeChange,
} from "../../services/negotiationBridge";
import {
  parseNegotiationBalancePreview,
  type NegotiationBalancePreview,
} from "../../services/negotiationBalance";
import {
  BalanceChip,
  balanceShortfall,
  coarseBalanceShortfall,
  hasNegotiationAccount,
  NegotiationBalanceActionsContext,
  NegotiationShortfall,
  RechargeFlow,
  useNegotiationBalance,
  useNegotiationBalanceActions,
  type NegotiationBalanceActions,
} from "../../components/NegotiationBalance";
import { InfoPopup } from "../../components/InfoPopup";
import { CompetitionExcludedMarker } from "../../components/CompetitionExcluded";
import { ResolvedAvatar, ResolvedOrgAvatarImg } from "../../components/ResolvedAvatar";
import { useNavigate } from "react-router-dom";
import { useAnimatedBids } from "./negotiate.animation";
import {
  BidCreationUnavailableError,
  BID_ATTEMPT_RESOLVED_EVENT,
  captureBidContext,
  isRecordedBidRefusal,
  negotiationBalanceRefusal,
  PendingBidError,
  StaleBidAttemptError,
  submitBidAttempt,
  supportsBidCreation,
  usePendingBidAttempts,
} from "./negotiate.bidAttempt";
import "./negotiate.css";

interface PostCardsProps {
  transferFeeCents?: number;
  id: string;
  username: string;
  position: number;
  lastBidAmount: number;
  lastBidOrg?: string;
  lastBidDiff?: number;
  avatar?: string;
  orgAvatar?: string;
  lastBidDate: string;
  orgTax: number;
}

interface PostCardsDetailsProps extends PostCardsProps {
  callbackClose: () => void;
  userInfo: { iss: string; org_id: string; exp: number } | undefined;
  bidWindowStart?: number;
  onBidLanded?: () => void;
}

interface HallOfCardsProps {
  cards: PostCardsProps[];
  hallFilter: "hits" | "flops";
  top1Id: string;
  top2Id: string;
  top3Id: string;
  flop1Id: string;
  flop2Id: string;
  flop3Id: string;
  powerOffensiveIds: string[];
  selectedBid: number;
  turboMode?: boolean;
  bidWindowStart?: number;
  // A bid answered 200 has landed: reread the board so it shows even when the
  // addNewBid event is late or lost.
  onBidLanded?: () => void;
}

interface CoinOrgAvatarProps {
  oddOrgAvatar: string;
  evenOrgAvatar: string;
}

interface ClassHighlightTypes {
  type: "hits" | "flops" | "power-offensive" | "2+";
  container: string;
  post: string;
  userInfo: string;
  postPosition: string;
  reasonPosition: string;
  chipContainer: string;
  chipContent: string;
  chipPosition: string;
  chipValue: string;
}

interface OrgStats {
  adultOnly: boolean;
  defaulter: boolean;
  offensiveAmount: number;
  autoBuy: boolean;
  sponsorMinorAudience?: boolean;
  settlementMode?: "prepaid" | "legacy";
  activeOrg: boolean;
  competitionRank?: number;
  bidDiscountPercent: number;
  monopolyFeeWaived: boolean;
  /** Out of the offensive competition (Ads ADR 0018); absent from an Ads that predates it. */
  competitionExcluded?: boolean;
}

interface CompetitionPrize {
  rank: number;
  bidDiscountPercent: number;
  monopolyFeeWaived: boolean;
}

interface OffensiveCompetition {
  id: string;
  status: "collecting" | "awaiting_reward_market" | "reward_market";
  startsAt: string;
  endsAt: string;
  nextTransitionAt?: string;
  rewardCycleId?: string;
  currentOrgReward?: CompetitionPrize;
  prizes: CompetitionPrize[];
}

function formatBRL(value: number): string {
  return value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Whether the company is out of the offensive competition. Ads keeps DaKasa's
 * own company out of it (Ads ADR 0018): it bids, but earns no ofensiva, has no
 * ranking position and wins no monthly prize. Ads answers it on /org-status as
 * `competitionExcluded`, derived from the Payments billing CNPJ; before that
 * answer, or from an Ads that predates the field, the console's own CNPJ check
 * (`isDaKasaOrg`) stands in. The tour always runs on mock data, where every
 * company competes.
 */
export function isCompetitionExcluded(
  serverAnswer: boolean | undefined,
  isDaKasaOrg: boolean,
): boolean {
  return (serverAnswer ?? isDaKasaOrg) && !isMockNegotiation();
}

export function chargePreviewValue(data: any, bidValue: number): number {
  const rawCents = data?.chargeAmountCents;
  if (rawCents !== undefined && rawCents !== null) {
    const cents = Number(rawCents);
    if (
      (typeof rawCents !== "number" && typeof rawCents !== "string") ||
      String(rawCents).trim() === "" ||
      !Number.isSafeInteger(cents) ||
      cents < 0
    )
      throw new Error("Invalid charge preview");
    return cents / 100;
  }
  // Legacy responses carry a multiplier; absent or malformed quotes must not
  // silently look like a free bid (Number(null) and Number("") both yield 0).
  const multiplier = Number(data?.orgTax);
  if (!Number.isFinite(multiplier) || multiplier < 1) throw new Error("Invalid charge preview");
  return multiplier * bidValue;
}

// Keep animation ticks inside the price label. Re-rendering the whole board
// for each real would also repeat layout measurements and every avatar render.
const AnimatedBidValue = React.memo(({ value }: { value: number }) => {
  const displayed = useAnimatedBids({ value });
  return <span aria-hidden="true">R${displayed.value}</span>;
});

const CircularText = ({ text = "DAKASA LTDA   •   CARREGANDO   •   " }) => {
  return (
    <div className="circleContainer">
      <div className="circle">
        {text.split("").map((char, i) => (
          <span key={i} style={{ transform: `rotate(${i * (360 / text.length)}deg)` }}>
            {char}
          </span>
        ))}
      </div>
    </div>
  );
};

const Navbar: React.FC<{ selected: "atrio" | "ranking" | "history" }> = ({ selected }) => {
  const navigate = useNavigate();

  function navigateIfIsSelected(tab: string) {
    if (tab === selected) return;
    const tableNavigate: { [key: string]: string } = {
      atrio: "/negociar",
      ranking: "/negociar/ranking",
      history: "/negociar/historico",
    };

    navigate(tableNavigate[tab]);
  }

  return (
    <nav id="negotiateNavbar">
      <img src="../assets/Logo_DAKASA_V1_Branco.png" alt="DaKasa" />
      <div
        className={`${"atrio" === selected ? `selectedTabPage ` : ""}tabPageTitle`}
        id="negotiateTabPageAtrio"
        onClick={() => navigateIfIsSelected("atrio")}
      >
        <StarRounded sx={{ fontSize: "36px" }} />
        <span>Átrio</span>
      </div>
      <div
        className={`${"ranking" === selected ? `selectedTabPage ` : ""}tabPageTitle`}
        id="negotiateTabPageRanking"
        onClick={() => navigateIfIsSelected("ranking")}
      >
        <EqualizerRounded sx={{ fontSize: "36px" }} />
        <span>Ranking</span>
      </div>
      <div
        className={`${"history" === selected ? `selectedTabPage ` : ""}tabPageTitle`}
        id="negotiateTabPageHistory"
        onClick={() => navigateIfIsSelected("history")}
      >
        <HistoryRounded sx={{ fontSize: "36px" }} />
        <span>Histórico</span>
      </div>
      <div className="tabPageTitle" id="negotiateTabPageBack" onClick={() => navigate("/")}>
        <ReplyRounded sx={{ fontSize: "36px" }} />
        <span>Voltar</span>
      </div>
    </nav>
  );
};

const FireComponent: React.FC<{ className?: string }> = ({ className }) => {
  return (
    <div className={`fire ${className}`}>
      <div className="fire-left">
        <div className="main-fire"></div>
        <div className="particle-fire"></div>
      </div>
      <div className="fire-center">
        <div className="main-fire"></div>
        <div className="particle-fire"></div>
      </div>
      <div className="fire-right">
        <div className="main-fire"></div>
        <div className="particle-fire"></div>
      </div>
      <div className="fire-bottom">
        <div className="main-fire"></div>
      </div>
    </div>
  );
};

// Mirrors the backend's `oneof=spam harassment hate_speech violence
// inappropriate other` validation on POST /negotiate/report/:id. The value
// sent to the API must be exactly one of these; only the label is pt-BR.
const REPORT_REASONS: { value: string; label: string }[] = [
  { value: "spam", label: "Spam" },
  { value: "harassment", label: "Assédio" },
  { value: "hate_speech", label: "Discurso de ódio" },
  { value: "violence", label: "Violência" },
  { value: "inappropriate", label: "Conteúdo impróprio" },
  { value: "other", label: "Outro" },
];

// A long caption opens in place: the panel's middle region scrolls while the
// bid and the footer stay put below it.
const PostCaption: React.FC<{ text: string }> = ({ text }) => {
  const [expanded, setExpanded] = React.useState(false);
  const [overflows, setOverflows] = React.useState(false);
  const textRef = React.useRef<HTMLParagraphElement>(null);
  React.useLayoutEffect(() => setExpanded(false), [text]);
  React.useLayoutEffect(() => {
    const element = textRef.current;
    if (!element || expanded) return;
    setOverflows(element.scrollHeight > element.clientHeight + 1);
  }, [text, expanded]);
  return (
    <div className="hallPostDetailsCaption">
      <p
        ref={textRef}
        className={`hallPostDetailsCaptionText${expanded ? "" : " hallPostDetailsCaptionClamped"}`}
      >
        {text}
      </p>
      {(overflows || expanded) && (
        <button
          type="button"
          className="hallPostDetailsCaptionToggle"
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded ? "Mostrar menos" : "Ver texto completo"}
        </button>
      )}
    </div>
  );
};

export const PostDetails: React.FC<PostCardsDetailsProps> = (props) => {
  const [finalBid, setFinalBid] = React.useState<number>();
  const [lastBidOrg, setLastBidOrg] = React.useState<string>();
  const [previewTaxValue, setPreviewTaxValue] = React.useState<number>(0);
  const [previewDiscountPercent, setPreviewDiscountPercent] = React.useState<number>(0);
  const [previewFeeWaived, setPreviewFeeWaived] = React.useState<boolean>(false);
  const [previewTransferFeeCents, setPreviewTransferFeeCents] = React.useState<number>();
  // Saldo de Negociações answer for this exact bid (prepaid rail only).
  const [previewBalance, setPreviewBalance] = React.useState<NegotiationBalancePreview>();
  const balanceActions = useNegotiationBalanceActions();
  const [loadingTaxValue, setLoadingTaxValue] = React.useState<boolean>(false);
  const [errorTaxValue, setErrorTaxValue] = React.useState<boolean>(false);
  const [waitingBidTime, setWaitingBidTime] = React.useState<{
    [key: string]: number;
  }>({});
  const [loadStatus, setLoadStatus] = React.useState<"loading" | "error" | "success">("loading");
  const [postContent, setPostContent] = React.useState<{
    postText?: string;
    postMedia?: string;
    postMediaType?: string;
  }>();

  const waitBidTimeout = React.useRef<{ [key: string]: NodeJS.Timeout }>({});
  const [bandwidthMbps] = React.useState(() => getBandwidthMbps());
  const bidInFlightRef = React.useRef(false);
  const [bidSubmitting, setBidSubmitting] = React.useState(false);
  // The amount the POST carries. A competitor's bid moves displayedBid while
  // the request is out, and the label must keep naming what was sent.
  const [submittedBid, setSubmittedBid] = React.useState<number>();
  const [bidError, setBidError] = React.useState("");
  const [reportError, setReportError] = React.useState("");

  const { permissions } = usePermissions();
  const canBid = checkPermission(permissions.bid, "create");
  const pending = usePendingBidAttempts(props.userInfo, props.id ? [props.id] : []);
  const pendingAttempt = pending.attempts[props.id];
  const [reportOpen, setReportOpen] = React.useState(false);
  const [reportReason, setReportReason] = React.useState("");
  const [reportDescription, setReportDescription] = React.useState("");
  const [reportSubmitting, setReportSubmitting] = React.useState(false);
  const reportInFlightRef = React.useRef(false);
  const lifecycleRef = React.useRef(0);
  const taxRequestRef = React.useRef<AbortController>();
  const taxRefreshTimerRef = React.useRef<ReturnType<typeof setTimeout>>();
  const readRequestRef = React.useRef<AbortController>();
  const mutationRequestsRef = React.useRef(new Set<AbortController>());
  const lastConfirmedRef = React.useRef<PostCardsProps>();
  const snapshotModeRef = React.useRef(getNegotiationModeRevision());
  React.useEffect(
    () =>
      onNegotiationModeChange(() => {
        // JWT/storage/Web Lock awaits can finish before React unmounts us.
        // Abort at the boundary itself; frozen financial intents remain saved.
        mutationRequestsRef.current.forEach((request) => request.abort());
      }),
    [],
  );

  function closeReportModal() {
    // Guard the close paths (X, Escape, Cancelar) so a report already in
    // flight can't be abandoned mid-submit, and reset the form so a later
    // open never resurfaces a stale reason/description.
    if (reportInFlightRef.current) return;
    setReportOpen(false);
    setReportReason("");
    setReportDescription("");
    setReportError("");
  }

  async function createReport() {
    if (
      !isNegotiationModeReady() ||
      snapshotModeRef.current !== getNegotiationModeRevision() ||
      reportInFlightRef.current ||
      !reportReason
    )
      return false;
    reportInFlightRef.current = true;
    const lifecycle = lifecycleRef.current;
    const revision = snapshotModeRef.current;
    const request = new AbortController();
    const post = negotiateAPI.post;
    mutationRequestsRef.current.add(request);
    setReportSubmitting(true);
    setReportError("");
    try {
      const JWT = (await localforage.getItem("JWT")) as string | undefined;
      if (
        request.signal.aborted ||
        lifecycle !== lifecycleRef.current ||
        !isNegotiationModeReady() ||
        revision !== getNegotiationModeRevision()
      )
        return false;
      if (!JWT) throw new Error("Session unavailable");
      await post(
        `/report/${props.id}`,
        { reason: reportReason, description: reportDescription },
        { headers: { "X-JWT": JWT }, signal: request.signal, timeout: 15_000 },
      );
      if (lifecycle !== lifecycleRef.current || revision !== getNegotiationModeRevision())
        return false;
      setReportOpen(false);
      setReportReason("");
      setReportDescription("");
    } catch (error) {
      if (request.signal.aborted || lifecycle !== lifecycleRef.current) return false;
      setReportError(
        getUserFacingError(error, {
          fallback: "Não foi possível enviar a denúncia. Confira sua conexão e tente novamente.",
        }),
      );
      return false;
    } finally {
      mutationRequestsRef.current.delete(request);
      if (lifecycle === lifecycleRef.current) {
        reportInFlightRef.current = false;
        setReportSubmitting(false);
      }
    }
  }

  class tax {
    static async calculateTax(value: number, lastOrg: string) {
      clearTimeout(taxRefreshTimerRef.current);
      taxRequestRef.current?.abort();
      const request = new AbortController();
      const get = negotiateAPI.get;
      const revision = snapshotModeRef.current;
      taxRequestRef.current = request;
      setPreviewTaxValue(0);
      setPreviewDiscountPercent(0);
      setPreviewFeeWaived(false);
      setPreviewTransferFeeCents(undefined);
      setPreviewBalance(undefined);
      setErrorTaxValue(false);
      if (!props.id || !props.userInfo || props.userInfo.org_id === lastOrg) {
        setLoadingTaxValue(false);
        return false;
      }

      setLoadingTaxValue(true);
      setErrorTaxValue(false);

      try {
        const JWT = (await localforage.getItem("JWT")) as string | undefined;
        if (!JWT || request.signal.aborted || revision !== getNegotiationModeRevision())
          return false;

        const headers: { [key: string]: string | undefined } = {
          "X-JWT": JWT,
        };

        const response = await get(`/org-tax-preview`, {
          headers,
          signal: request.signal,
          timeout: 10_000,
          // postId lets Ads quote this post's own tax bound for the balance check.
          params: {
            value: value,
            postId: props.id,
          },
        });
        if (request.signal.aborted || revision !== getNegotiationModeRevision()) return false;
        // org_tax is a MULTIPLIER on the bid (enterprise-ads-api bid_lua.go), not
        // an amount added to it. The biller charges bid * org_tax
        // (payments-api msg.go), so the "Total:" the company sees before it bids
        // must be bid * org_tax too — "100 + 16 = 116" on screen while the engine
        // charges 100 * 16 = 1600 is the panel-vs-bill lie this ends.
        setPreviewTaxValue(chargePreviewValue(response.data, value));
        setPreviewDiscountPercent(Number(response.data.bidDiscountPercent || 0));
        setPreviewFeeWaived(Boolean(response.data.monopolyFeeWaived));
        const transferFee = response.data.transferFeeCents;
        if (transferFee !== undefined && (!Number.isSafeInteger(transferFee) || transferFee < 0))
          throw new Error("Invalid transfer fee preview");
        setPreviewTransferFeeCents(transferFee);
        setPreviewBalance(parseNegotiationBalancePreview(response.data.negotiationBalance));
      } catch (error) {
        if (request.signal.aborted) return false;
        setErrorTaxValue(true);
        return false;
      } finally {
        if (!request.signal.aborted) setLoadingTaxValue(false);
      }
    }
  }

  class socketHooks {
    static addNewBid(p: PostCardsProps) {
      if (p.id !== props.id) {
        if (!props.id || !lastConfirmedRef.current) return;
        // A different winning post can change the monopoly multiplier here.
        // Abort the old quote immediately, but coalesce a burst of live bids.
        taxRequestRef.current?.abort();
        clearTimeout(taxRefreshTimerRef.current);
        setLoadingTaxValue(true);
        taxRefreshTimerRef.current = setTimeout(() => {
          const confirmed = lastConfirmedRef.current;
          if (!confirmed) return;
          void tax.calculateTax(
            confirmed.lastBidAmount + (props.lastBidDiff ?? 0),
            confirmed.lastBidOrg || "",
          );
        }, 750);
        return;
      }
      if (!isCurrentBid(lastConfirmedRef.current, p, props.bidWindowStart)) return;
      lastConfirmedRef.current = p;
      const lastBid = p.lastBidAmount + (props.lastBidDiff ?? 0);
      const lastOrg = p.lastBidOrg || "";
      setFinalBid(lastBid);
      setLastBidOrg(lastOrg);
      tax.calculateTax(lastBid, lastOrg);

      const remaining = remainingBidCooldown(p.lastBidDate);
      setWaitingBidTime((prev) => {
        const next = { ...prev };
        if (remaining) next[props.id] = remaining;
        else delete next[props.id];
        return next;
      });
      if (remaining) {
        countdownBid();
      } else {
        clearInterval(waitBidTimeout.current[props.id]);
        delete waitBidTimeout.current[props.id];
      }
    }
  }

  async function getPostDetails() {
    if (!props.id) return;
    setLoadStatus("loading");
    const request = new AbortController();
    const get = negotiateAPI.get;
    const revision = snapshotModeRef.current;
    readRequestRef.current?.abort();
    readRequestRef.current = request;
    const JWT = (await localforage.getItem("JWT")) as string | undefined;
    if (!JWT || request.signal.aborted || revision !== getNegotiationModeRevision()) return false;

    const headers: { [key: string]: string | undefined } = {
      "X-JWT": JWT,
    };

    try {
      const response = await get(`/post/${props.id}`, {
        headers: headers,
        signal: request.signal,
        timeout: 10_000,
      });
      if (request.signal.aborted || revision !== getNegotiationModeRevision()) return false;
      setPostContent(response.data);
      setLoadStatus("success");
    } catch (error) {
      if (request.signal.aborted) return false;
      setLoadStatus("error");
      return false;
    }
  }

  async function createLastBid() {
    // Guard against double-submit: two rapid clicks (or turbo-mode firing on
    // top of a click) would otherwise POST /bid twice before the server
    // cooldown is reflected locally, producing ambiguous submission feedback.
    if (
      !isNegotiationModeReady() ||
      snapshotModeRef.current !== getNegotiationModeRevision() ||
      !canBid ||
      pending.error ||
      bidInFlightRef.current ||
      (!pendingAttempt &&
        (waitingBidTime[props.id] > 0 || lastBidOrg === props.userInfo?.org_id)) ||
      // The balance cannot admit this new bid. A frozen attempt can still be
      // consulted: replaying its key never reserves anything new.
      (!pendingAttempt && balanceShortfall(previewBalance) !== null) ||
      !props.userInfo
    )
      return false;
    bidInFlightRef.current = true;
    const lifecycle = lifecycleRef.current;
    const revision = snapshotModeRef.current;
    const amount = pendingAttempt?.payload.lastBidAmount ?? displayedBid;
    if (!Number.isFinite(amount) || amount <= 0) {
      bidInFlightRef.current = false;
      return false;
    }
    const request = new AbortController();
    const session = { ...props.userInfo };
    const context = captureBidContext(session, props.id);
    mutationRequestsRef.current.add(request);
    setBidError("");
    setSubmittedBid(amount);
    setBidSubmitting(true);
    try {
      const JWT = (await localforage.getItem("JWT")) as string | undefined;
      if (
        request.signal.aborted ||
        lifecycle !== lifecycleRef.current ||
        !isNegotiationModeReady() ||
        revision !== getNegotiationModeRevision()
      )
        return false;
      if (!JWT) throw new Error("Session unavailable");
      await submitBidAttempt(
        session,
        {
          // The coordinated Ads change checks this candidate before reserving
          // a new bid. A consultation reuses the original candidate and key.
          lastBidAmount: amount,
          lastBidDiff: props.lastBidDiff ?? 0,
          postId: props.id,
        },
        {
          params: { bandwidth: bandwidthMbps },
          headers: { "X-JWT": JWT },
          signal: request.signal,
          timeout: 15_000,
        },
        (attempt) => {
          if (lifecycle === lifecycleRef.current) setSubmittedBid(attempt.payload.lastBidAmount);
        },
        { context, expectedAttempt: pendingAttempt },
      );
      props.onBidLanded?.();
      balanceActions?.refresh();
    } catch (error) {
      if (request.signal.aborted || lifecycle !== lifecycleRef.current) return false;
      if (isRecordedBidRefusal(error)) props.onBidLanded?.();
      // Every answer can move the balance. A balance refusal also re-reads
      // this bid's preview: a replayed refusal body never carries an amount.
      balanceActions?.refresh();
      if (negotiationBalanceRefusal(error instanceof PendingBidError ? error.original : error))
        void tax.calculateTax(amount, lastBidOrg || "");
      setBidError(
        error instanceof PendingBidError ||
          error instanceof BidCreationUnavailableError ||
          error instanceof StaleBidAttemptError
          ? error.message
          : getUserFacingError(error, {
              context: "bid",
              fallback:
                "Não foi possível confirmar seu lance. Confira o lance atual antes de tentar novamente.",
            }),
      );
      return false;
    } finally {
      mutationRequestsRef.current.delete(request);
      if (lifecycle === lifecycleRef.current) {
        bidInFlightRef.current = false;
        setBidSubmitting(false);
      }
    }
  }

  function countdownBid() {
    if (waitBidTimeout.current[props.id]) return;
    waitBidTimeout.current[props.id] = setInterval(() => {
      setWaitingBidTime((prev) => {
        if (!prev) return prev;
        const newWaitingBidTime = { ...prev };
        if ((newWaitingBidTime[props.id] || 0) <= 1) {
          clearInterval(waitBidTimeout.current[props.id]);
          delete waitBidTimeout.current[props.id];
          delete newWaitingBidTime[props.id];
          return newWaitingBidTime;
        }

        newWaitingBidTime[props.id] = newWaitingBidTime[props.id] - 1;
        return newWaitingBidTime;
      });
    }, 1010);
  }

  function startInitialCountdownIfNecessary() {
    const remaining = remainingBidCooldown(props.lastBidDate);
    if (!remaining) return;

    setWaitingBidTime((prev) => {
      if (!prev) return prev;
      const newWaitingBidTime = { ...prev };
      newWaitingBidTime[props.id] = remaining;
      return newWaitingBidTime;
    });
    countdownBid();
  }

  React.useEffect(() => {
    lifecycleRef.current += 1;
    setReportOpen(false);
    setReportReason("");
    setReportDescription("");
    setReportError("");
    setBidError("");
    setReportSubmitting(false);
    setBidSubmitting(false);
    reportInFlightRef.current = false;
    bidInFlightRef.current = false;
    lastConfirmedRef.current = {
      ...props,
      lastBidAmount: props.lastBidAmount - (props.lastBidDiff ?? 0),
    };
    setPostContent(undefined);
    setWaitingBidTime({});
    startInitialCountdownIfNecessary();
    getPostDetails();
    return () => {
      lifecycleRef.current += 1;
      taxRequestRef.current?.abort();
      clearTimeout(taxRefreshTimerRef.current);
      readRequestRef.current?.abort();
      mutationRequestsRef.current.forEach((request) => request.abort());
      Object.values(waitBidTimeout.current).forEach(clearInterval);
      waitBidTimeout.current = {};
    };
  }, [props.id]);

  React.useEffect(() => {
    RTA.on("addNewBid", socketHooks.addNewBid);
    return () => RTA.off("addNewBid", socketHooks.addNewBid);
  }, [props.id, props.lastBidDiff, props.userInfo?.org_id, props.bidWindowStart]);

  // A recharge or another bid of the company moved the Saldo de Negociações:
  // quote this bid again so the shortfall (and the disabled button) follow.
  const requoteRef = React.useRef<() => void>(() => {});
  requoteRef.current = () => {
    const confirmed = lastConfirmedRef.current;
    if (!confirmed) return;
    void tax.calculateTax(
      confirmed.lastBidAmount + (props.lastBidDiff ?? 0),
      confirmed.lastBidOrg || "",
    );
  };
  React.useEffect(() => {
    if (!props.id || !balanceActions) return;
    return balanceActions.subscribe(() => requoteRef.current());
  }, [props.id, balanceActions]);

  React.useEffect(() => {
    const incoming = {
      ...props,
      lastBidAmount: props.lastBidAmount - (props.lastBidDiff ?? 0),
    };
    if (isCurrentBid(lastConfirmedRef.current, incoming)) lastConfirmedRef.current = incoming;
    // HTTP board refreshes and socket events share the same confirmation fence.
    // Changing the increment derives a new candidate from that record only.
    const confirmed = lastConfirmedRef.current ?? incoming;
    const amount = confirmed.lastBidAmount + (props.lastBidDiff ?? 0);
    setFinalBid(amount);
    setLastBidOrg(confirmed.lastBidOrg || "");
    tax.calculateTax(amount, confirmed.lastBidOrg || "");
    const remaining = remainingBidCooldown(confirmed.lastBidDate);
    setWaitingBidTime(remaining ? { [props.id]: remaining } : {});
    if (remaining) countdownBid();
    else {
      clearInterval(waitBidTimeout.current[props.id]);
      delete waitBidTimeout.current[props.id];
    }
  }, [
    props.id,
    props.lastBidAmount,
    props.lastBidDiff,
    props.lastBidDate,
    props.lastBidOrg,
    props.orgTax,
    props.userInfo?.org_id,
  ]);

  // Closing this modal is purely a UI rewind: `callbackClose` undoes the local
  // bid *preview*, and opening it has no server-side counterpart to reverse
  // (PATCH /open/:id is idempotent). A bid that was already POSTed to /bid is
  // never revoked by closing — which is exactly why the exit control says
  // "Fechar" and not "Cancelar".
  const { callbackClose } = props;
  const dismiss = React.useCallback(() => {
    callbackClose();
    setPostContent(undefined);
  }, [callbackClose]);

  // A blocking overlay must be dismissable with Escape. Guarded on `props.id`
  // so the collapsed instance (which renders nothing) never steals the key.
  React.useEffect(() => {
    if (!props.id) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && !reportOpen) dismiss();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [props.id, dismiss, reportOpen]);

  if (props.id === "") return <></>;
  // Single source of truth for the bid amount: both the button label and the
  // POST /bid payload read `displayedBid`. The server still validates the bid
  // against its authoritative state; the UI does not settle a charge.
  const displayedBid = finalBid ?? props.lastBidAmount;
  // Payments still decides; this only stops a bid the balance cannot admit.
  const balanceBlocked = !pendingAttempt && balanceShortfall(previewBalance) !== null;
  // The confirmed bid the next one covers; zero before the first bid.
  const currentBid = Math.max(0, displayedBid - (props.lastBidDiff ?? 0));
  const companyLeads = Boolean(lastBidOrg) && lastBidOrg === props.userInfo?.org_id;
  const hasMedia = postContent?.postMediaType === "image" || postContent?.postMediaType === "video";
  const postText = postContent?.postText?.trim() ?? "";
  return (
    <>
      <div
        className="hallPostDetailsContainer"
        role="dialog"
        aria-modal="true"
        aria-label={`Lance na postagem de ${props.username}`}
        onClick={(e) => {
          // Only a click on the overlay itself dismisses. Clicks bubbling up from
          // the panel must not close the modal. Otherwise a near-miss on the bid
          // button would throw the user out of the dialog.
          if (e.target === e.currentTarget) dismiss();
        }}
      >
        <div className="hallPostDetails">
          {/* The stage shows the media whole; a post without media puts its text here instead. */}
          <div className="hallPostDetailsStage">
            {loadStatus === "loading" ? (
              <div className="loadingPostDetails">
                <CircularProgress color="warning" />
              </div>
            ) : loadStatus === "error" ? (
              <span className="hallPostDetailsError">Erro ao carregar o post</span>
            ) : postContent?.postMediaType === "image" ? (
              <img src={postContent.postMedia} alt={`Mídia da postagem de ${props.username}`} />
            ) : postContent?.postMediaType === "video" ? (
              <video controls>
                <source src={postContent.postMedia} type="video/mp4" />
                Seu navegador não reproduz este vídeo.
              </video>
            ) : (
              <div className="hallPostDetailsStageText">
                <p>{postText || "Esta postagem não tem texto."}</p>
              </div>
            )}
          </div>
          <div className="hallPostDetailsPanel">
            {/* Only this region scrolls: the bid and the footer stay in place below it. */}
            <div className="hallPostDetailsScroll">
              <div className="hallPostDetailsHeader">
                <ResolvedAvatar
                  src={props.avatar}
                  alt={props.username}
                  sx={{ width: 48, height: 48 }}
                />
                <div className="hallPostDetailsAuthor">
                  <span className="hallPostDetailsUsername">{props.username}</span>
                  <span
                    className={`hallPostDetailsPosition hallPostDetailsPosition${
                      props.position >= 1 && props.position <= 3 ? props.position : "Other"
                    }`}
                  >
                    #{props.position} no Átrio
                  </span>
                </div>
              </div>
              {loadStatus === "success" && hasMedia && postText && <PostCaption text={postText} />}
              <div className="hallPostDetailsLead">
                <span className="hallPostDetailsSectionLabel">Lance atual</span>
                <div className="hallPostDetailsLeadRow">
                  {currentBid > 0 && props.orgAvatar && (
                    <ResolvedAvatar src={props.orgAvatar} alt="" sx={{ width: 36, height: 36 }} />
                  )}
                  <span className="hallPostDetailsLeadValue">
                    {currentBid > 0 ? `R$ ${formatBRL(currentBid)}` : "Nenhum lance ainda"}
                  </span>
                  {currentBid > 0 && !companyLeads && (
                    <span className="hallPostDetailsLeadStatus">Outra empresa lidera</span>
                  )}
                </div>
              </div>
            </div>
            {/* The bid error sits next to the bid. As a grid child, even collapsed, it took a cell. */}
            <BannerFeedback
              type="error"
              open={Boolean(bidError)}
              message={bidError}
              callback={() => setBidError("")}
            />
            <div className="hallPostDetailsBid">
              <span className="negotiateVisuallyHidden" role="status">
                {bidSubmitting ? `Enviando lance de R$${submittedBid ?? displayedBid}` : ""}
              </span>
              {pending.error ? (
                <div className="hallPostDetailsBidNotice" role="alert">
                  <span>
                    Não foi possível consultar seus lances enviados. Recarregue a página antes de
                    continuar.
                  </span>
                </div>
              ) : !canBid ? (
                <div className="hallPostDetailsBidNotice">
                  <span>Seu cargo permite consultar as publicações, mas não enviar lances.</span>
                </div>
              ) : !pendingAttempt && !supportsBidCreation() ? (
                <div className="hallPostDetailsBidNotice" role="status">
                  <span>
                    Para enviar lances, use um navegador atualizado em uma conexão segura.
                  </span>
                </div>
              ) : !pendingAttempt && waitingBidTime[props.id] > 0 ? (
                <div className="hallPostDetailsBidNotice hallPostDetailsBidWait">
                  <AccessTimeRounded sx={{ fontSize: "20px" }} aria-hidden="true" />
                  <span>Próximo lance em {waitingBidTime[props.id]} s</span>
                </div>
              ) : !pendingAttempt && companyLeads ? (
                <div className="hallPostDetailsBidNotice hallPostDetailsBidLeading">
                  <CheckCircleOutlineRounded sx={{ fontSize: "24px" }} aria-hidden="true" />
                  <span>Sua empresa lidera esta postagem</span>
                </div>
              ) : (
                <>
                  <button
                    className="hallPostDetailsActionsButtonBid"
                    id={`chipPosDoBid${props.position}`}
                    onClick={createLastBid}
                    disabled={bidSubmitting || !props.userInfo || balanceBlocked}
                    data-balance-blocked={balanceBlocked ? "true" : undefined}
                  >
                    {bidSubmitting ? (
                      <>
                        <CircularProgress color="inherit" size={20} aria-hidden="true" />
                        Enviando R${submittedBid ?? displayedBid}…
                      </>
                    ) : pendingAttempt ? (
                      "Consultar lance enviado"
                    ) : (
                      `Dar lance de R$${displayedBid}`
                    )}
                  </button>
                  <div className="hallPostDetailsActionsTotalBid">
                    <span>Total</span>
                    {loadingTaxValue ? (
                      <CircularProgress size={12} sx={{ color: "var(--neutral-11)" }} />
                    ) : errorTaxValue ? (
                      <span className="hallPostDetailsTotalUnavailable">---</span>
                    ) : (
                      <span className="hallPostDetailsTotalValue">
                        R${formatBRL(previewTaxValue)}
                      </span>
                    )}
                  </div>
                  {previewTransferFeeCents !== undefined && !loadingTaxValue && !errorTaxValue && (
                    <span className="hallPostDetailsFeeLine">
                      Inclui tarifa de transferência: R$ {formatBRL(previewTransferFeeCents / 100)}
                    </span>
                  )}
                  {previewDiscountPercent > 0 && (
                    <span className="hallPostDetailsPrizePreview">
                      {previewDiscountPercent}% OFF
                      {previewFeeWaived ? " · sem taxa de monopólio" : ""}
                    </span>
                  )}
                  {previewBalance && !pendingAttempt && (
                    <NegotiationShortfall
                      preview={previewBalance}
                      onRecharge={
                        balanceActions?.canRecharge && canBid
                          ? balanceActions.openRecharge
                          : undefined
                      }
                    />
                  )}
                </>
              )}
            </div>
            <div className="hallPostDetailsFooter">
              {/*
                Gate on bid/view, not on a dedicated report capability. Reaching
                this negotiation already requires bid/view (Root gates /negociar on
                it), and roles created before the report capability existed come
                back with report: []. The backend uses the same bid/view check:
                reading the negotiation is sufficient; placing a bid is not required.
              */}
              {checkPermission(permissions.bid, "view") && (
                <button
                  type="button"
                  onClick={() => setReportOpen(true)}
                  className="hallPostDetailsActionsButtonReport"
                  id={`reportPostDetailsButton${props.position}`}
                >
                  <OutlinedFlagRounded sx={{ fontSize: "18px" }} aria-hidden="true" />
                  Denunciar
                </button>
              )}
              <div className="hallPostDetailsFooterClose">
                <span className="hallPostDetailsEscHint" aria-hidden="true">
                  Esc fecha
                </span>
                <button
                  type="button"
                  onClick={dismiss}
                  className="hallPostDetailsActionsButtonClose"
                  id={`cancelPostDetailsButton${props.position}`}
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {reportOpen && (
        <FloatContainer className="hallReportModal" close={closeReportModal}>
          <div className="hallReportModalContent">
            <BannerFeedback
              type="error"
              open={Boolean(reportError)}
              message={reportError}
              callback={() => setReportError("")}
            />
            <h3 className="hallReportModalTitle">Denunciar publicação</h3>
            <label className="hallReportReasonField" htmlFor="hallReportReasonSelect">
              Motivo
            </label>
            <select
              id="hallReportReasonSelect"
              className="hallReportReasonSelect"
              value={reportReason}
              disabled={reportSubmitting}
              onChange={(event) => setReportReason(event.target.value)}
            >
              <option value="" disabled>
                Selecione o motivo
              </option>
              {REPORT_REASONS.map((reason) => (
                <option key={reason.value} value={reason.value}>
                  {reason.label}
                </option>
              ))}
            </select>
            <label className="hallReportReasonField" htmlFor="hallReportDescriptionField">
              Detalhes (opcional)
            </label>
            <textarea
              id="hallReportDescriptionField"
              className="hallReportDescriptionField"
              placeholder="Descreva o motivo (opcional)"
              rows={3}
              value={reportDescription}
              disabled={reportSubmitting}
              maxLength={1000}
              onChange={(event) => setReportDescription(event.target.value)}
            />
            <div className="hallReportModalActions">
              <button
                type="button"
                className="hallReportModalButtonCancel"
                onClick={closeReportModal}
                disabled={reportSubmitting}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="hallReportModalButtonConfirm"
                onClick={createReport}
                disabled={reportSubmitting || !reportReason}
              >
                Confirmar
              </button>
            </div>
          </div>
        </FloatContainer>
      )}
    </>
  );
};

const FakeLoadingComponent: React.FC<{ loading: boolean }> = (props) => {
  const [hidden, setHidden] = React.useState(false);

  React.useEffect(() => {
    if (props.loading) {
      setHidden(false);
      return;
    }

    const timer = setTimeout(() => setHidden(true), 500);
    return () => clearTimeout(timer);
  }, [props.loading]);

  if (hidden) return null;
  return (
    <div
      id="negotiateInitialLoading"
      style={{
        opacity: props.loading ? 1 : 0,
      }}
    >
      <div
        className="loadingContainer"
        style={{
          marginTop: props.loading ? "0" : "-50px",
        }}
      >
        <CircularText />
        <FireComponent />
      </div>
    </div>
  );
};

export const HallOfCards: React.FC<HallOfCardsProps> = React.memo(
  (props) => {
    const snapshotModeRef = React.useRef(getNegotiationModeRevision());
    const { permissions } = usePermissions();
    const canBid = checkPermission(permissions.bid, "create");
    const turboEnabled = Boolean(props.turboMode && canBid && supportsBidCreation());
    const [openedCards, setOpenedCards] = React.useState<string[]>([]);
    const [selectedPost, setSelectedPost] = React.useState<PostCardsProps>();
    const [confirmedCards, setConfirmedCards] = React.useState<Record<string, PostCardsProps>>(() =>
      Object.fromEntries(props.cards.map((card) => [card.id, card])),
    );
    const [hoveredPostId, setHoveredPostId] = React.useState<string>();
    const [focusedPostId, setFocusedPostId] = React.useState<string>();
    const previewPostId = hoveredPostId ?? focusedPostId;
    const pointerFocusRef = React.useRef(false);
    function previewFocusHandlers(cardId: string) {
      return {
        onMouseDown: () => {
          pointerFocusRef.current = true;
          setFocusedPostId(undefined);
        },
        onMouseUp: () => {
          pointerFocusRef.current = false;
        },
        onFocus: () => {
          if (!pointerFocusRef.current) setFocusedPostId(cardId);
        },
        onBlur: () => {
          pointerFocusRef.current = false;
          setFocusedPostId((current) => (current === cardId ? undefined : current));
        },
      };
    }

    function clearPreview() {
      setHoveredPostId(undefined);
      setFocusedPostId(undefined);
    }
    const [currentCoinAvatarDegree, setCurrentCoinAvatarDegree] = React.useState<number[]>([]);
    const [coinOrgAvatar, setCoinOrgAvatar] = React.useState<CoinOrgAvatarProps[]>([]);
    const [lastBidOrg, setLastBidOrg] = React.useState<string[]>([]);
    const [waitingBidTime, setWaitingBidTime] = React.useState<{
      [key: string]: number;
    }>({});
    const [bandwidthMbps] = React.useState(() => getBandwidthMbps());
    const [taxQuote, setTaxQuote] = React.useState<{
      postId: string;
      amount: number;
      value?: number;
      error?: boolean;
      // Saldo de Negociações answer for this card's bid (prepaid rail only).
      balance?: NegotiationBalancePreview;
    }>();
    const [bidError, setBidError] = React.useState("");
    // The last turbo bid the balance could not admit, shown with its shortfall.
    // `atLeast` marks the coarse estimate of a bid without its own quote.
    const [balanceNotice, setBalanceNotice] = React.useState<{
      preview: NegotiationBalancePreview;
      atLeast: boolean;
    }>();
    // Bumped when the balance moved, so the open preview is quoted again.
    const [quoteNonce, setQuoteNonce] = React.useState(0);
    const balanceActions = useNegotiationBalanceActions();
    React.useEffect(() => {
      if (!balanceActions) return;
      return balanceActions.subscribe(() => setQuoteNonce((nonce) => nonce + 1));
    }, [balanceActions]);
    const [userInfo, setUserInfo] = React.useState<PostCardsDetailsProps["userInfo"]>();
    const pending = usePendingBidAttempts(
      userInfo,
      props.cards.map((card) => card.id),
    );

    const openedCardsRef = React.useRef<string[]>([]);
    const waitBidTimeoutRef = React.useRef<{ [key: string]: NodeJS.Timeout }>({});
    const confirmedCardsRef = React.useRef(confirmedCards);
    const confirmedWindowStartRef = React.useRef(props.bidWindowStart);
    const lifecycleRef = React.useRef(0);
    const initializationRef = React.useRef(0);
    const requestControllersRef = React.useRef(new Set<AbortController>());
    const openInFlightRef = React.useRef(new Set<string>());
    const coinTimersRef = React.useRef(new Set<ReturnType<typeof setTimeout>>());
    const coinFinalDegreeRef = React.useRef<number[]>([]);
    const coinMaskDegreeRef = React.useRef<number[]>([]);
    const hasCoinTimeoutRef = React.useRef<boolean[]>([]);
    const finalCoinOrgAvatarRef = React.useRef<CoinOrgAvatarProps[]>([]);
    const lastBidOrgRef = React.useRef<string[]>([]);
    const userInfoRef = React.useRef<{ iss: string; org_id: string; exp: number } | undefined>();
    // Tracks bids currently in flight, keyed by post id. Turbo mode fires
    // createLastBid on repeated clicks, so without this guard a single
    // card could have several /bid POSTs racing before the server cooldown is
    // reflected locally.
    const bidInFlightRef = React.useRef<Set<string>>(new Set());
    // The same in-flight bids as state, so the card shows that its bid is being
    // sent: the ref above only blocks the second click, it never repaints.
    const [sendingBidIds, setSendingBidIds] = React.useState<ReadonlySet<string>>(() => new Set());
    // A bid outlives board refreshes. The 30s poll replaces props.cards, and
    // aborting the POST there threw away the answer of a bid that may have
    // landed, with nothing on screen. Only unmounting the board aborts it: a
    // route change, a new window, or the hard reset after an RTA reconnect.
    const bidRequestsRef = React.useRef(new Set<AbortController>());
    React.useEffect(() => {
      lifecycleRef.current += 1;
      const requests = bidRequestsRef.current;
      const unsubscribeMode = onNegotiationModeChange(() => {
        requests.forEach((request) => request.abort());
        requestControllersRef.current.forEach((request) => request.abort());
      });
      return () => {
        unsubscribeMode();
        lifecycleRef.current += 1;
        requests.forEach((request) => request.abort());
        requestControllersRef.current.forEach((request) => request.abort());
      };
    }, []);
    const bidTargets = Object.fromEntries(
      props.cards.map((card) => [
        card.id,
        displayedCardBid(
          (confirmedCards[card.id] ?? card).lastBidAmount,
          props.selectedBid,
          canBid &&
            (confirmedCards[card.id] ?? card).lastBidOrg !== userInfo?.org_id &&
            (turboEnabled || previewPostId === card.id),
        ),
      ]),
    );
    const selectedConfirmed = selectedPost ? confirmedCards[selectedPost.id] : undefined;
    const selectedCandidate = selectedConfirmed
      ? bidCandidate(selectedConfirmed.lastBidAmount, props.selectedBid)
      : undefined;
    const coinBaseSpin = 180;
    const highlightContainerClassNames: { [key: string]: ClassHighlightTypes } = {
      top1: {
        type: "hits",
        container: "highlightPostContainer highlightPostContainerTop1",
        post: "highlightPost highlightPostTop1",
        userInfo: "highlightUserInfo highlightUserInfoTop1",
        postPosition: "highlightPostPosition highlightPostPositionTop1",
        reasonPosition: "highlightReasonPosition highlightReasonPositionTop1",
        chipContainer: "hallPostItemContainer hallPostItemContainerTop1",
        chipContent: "hallPostItem hallPostItemTop1",
        chipPosition: "hallPostItemPosition hallPostItemPositionTop1",
        chipValue: "hallPostItemValue hallPostItemValueTop1",
      },
      top2: {
        type: "hits",
        container: "highlightPostContainer highlightPostContainerTop2",
        post: "highlightPost highlightPostTop2",
        userInfo: "highlightUserInfo highlightUserInfoTop2",
        postPosition: "highlightPostPosition highlightPostPositionTop2",
        reasonPosition: "highlightReasonPosition highlightReasonPositionTop2",
        chipContainer: "hallPostItemContainer hallPostItemContainerTop2",
        chipContent: "hallPostItem hallPostItemTop2",
        chipPosition: "hallPostItemPosition hallPostItemPositionTop2",
        chipValue: "hallPostItemValue hallPostItemValueTop2",
      },
      top3: {
        type: "hits",
        container: "highlightPostContainer highlightPostContainerTop3",
        post: "highlightPost highlightPostTop3",
        userInfo: "highlightUserInfo highlightUserInfoTop3",
        postPosition: "highlightPostPosition highlightPostPositionTop3",
        reasonPosition: "highlightReasonPosition highlightReasonPositionTop3",
        chipContainer: "hallPostItemContainer hallPostItemContainerTop3",
        chipContent: "hallPostItem hallPostItemTop3",
        chipPosition: "hallPostItemPosition hallPostItemPositionTop3",
        chipValue: "hallPostItemValue hallPostItemValueTop3",
      },
      flop1: {
        type: "flops",
        container: "highlightPostContainer highlightPostContainerFlop1",
        post: "highlightPost highlightPostFlop1",
        userInfo: "highlightUserInfo highlightUserInfoFlop1",
        postPosition: "highlightPostPosition highlightPostPositionFlop1",
        reasonPosition: "highlightReasonPosition highlightReasonPositionFlop1",
        chipContainer: "hallPostItemContainer hallPostItemContainerFlop1",
        chipContent: "hallPostItem hallPostItemFlop1",
        chipPosition: "hallPostItemPosition hallPostItemPositionFlop1",
        chipValue: "hallPostItemValue hallPostItemValueFlop1",
      },
      flop2: {
        type: "flops",
        container: "highlightPostContainer highlightPostContainerFlop2",
        post: "highlightPost highlightPostFlop2",
        userInfo: "highlightUserInfo highlightUserInfoFlop2",
        postPosition: "highlightPostPosition highlightPostPositionFlop2",
        reasonPosition: "highlightReasonPosition highlightReasonPositionFlop2",
        chipContainer: "hallPostItemContainer hallPostItemContainerFlop2",
        chipContent: "hallPostItem hallPostItemFlop2",
        chipPosition: "hallPostItemPosition hallPostItemPositionFlop2",
        chipValue: "hallPostItemValue hallPostItemValueFlop2",
      },
      flop3: {
        type: "flops",
        container: "highlightPostContainer highlightPostContainerFlop3",
        post: "highlightPost highlightPostFlop3",
        userInfo: "highlightUserInfo highlightUserInfoFlop3",
        postPosition: "highlightPostPosition highlightPostPositionFlop3",
        reasonPosition: "highlightReasonPosition highlightReasonPositionFlop3",
        chipContainer: "hallPostItemContainer hallPostItemContainerFlop3",
        chipContent: "hallPostItem hallPostItemFlop3",
        chipPosition: "hallPostItemPosition hallPostItemPositionFlop3",
        chipValue: "hallPostItemValue hallPostItemValueFlop3",
      },
      powerOffensive: {
        type: "power-offensive",
        container: "highlightPostContainer highlightPostContainerPowerOffensive",
        post: "highlightPost highlightPostPowerOffensive",
        userInfo: "highlightUserInfo highlightUserInfoPowerOffensive",
        postPosition: "highlightPostPosition highlightPostPositionPowerOffensive",
        reasonPosition: "highlightReasonPosition highlightReasonPositionPowerOffensive",
        chipContainer: "hallPostItemContainer hallPostItemContainerPowerOffensive",
        chipContent: "hallPostItem hallPostItemPowerOffensive",
        chipPosition: "hallPostItemPosition hallPostItemPositionPowerOffensive",
        chipValue: "hallPostItemValue hallPostItemValuePowerOffensive",
      },
    };

    class coinAvatar {
      static rotate(index: number, newAvatar: string) {
        if (!coinFinalDegreeRef.current || !coinMaskDegreeRef.current || !hasCoinTimeoutRef.current)
          return;
        const evenOrOdd = (coinFinalDegreeRef.current[index] / coinBaseSpin) % 2;
        coinFinalDegreeRef.current[index] += coinBaseSpin;

        if (evenOrOdd === 0) {
          finalCoinOrgAvatarRef.current[index].evenOrgAvatar = newAvatar;
        } else {
          finalCoinOrgAvatarRef.current[index].oddOrgAvatar = newAvatar;
        }

        if (hasCoinTimeoutRef.current[index]) return;
        this.coreRotate(index);
      }

      static changeAvatar(index: number, currentDegree: number) {
        if (!coinOrgAvatar) return;
        const oddOrEven = ((coinFinalDegreeRef.current[index] - coinBaseSpin) / coinBaseSpin) % 2;
        if (oddOrEven === 0) {
          if ((currentDegree / coinBaseSpin) % 2 !== 0) return;
          setCoinOrgAvatar((prev) => {
            if (!prev) return prev;
            const newCoin = [...prev];
            newCoin[index] = {
              ...newCoin[index],
              evenOrgAvatar: finalCoinOrgAvatarRef.current[index].evenOrgAvatar,
            };
            return newCoin;
          });
          return;
        }

        if ((currentDegree / coinBaseSpin) % 2 === 0) return;
        setCoinOrgAvatar((prev) => {
          if (!prev) return prev;
          const newCoin = [...prev];
          newCoin[index] = {
            ...newCoin[index],
            oddOrgAvatar: finalCoinOrgAvatarRef.current[index].oddOrgAvatar,
          };
          return newCoin;
        });
      }

      private static coreRotate(index: number) {
        if (!coinFinalDegreeRef.current || !coinMaskDegreeRef.current || !hasCoinTimeoutRef.current)
          return;
        const increment = 20;
        if (coinMaskDegreeRef.current[index] >= coinFinalDegreeRef.current[index]) {
          hasCoinTimeoutRef.current[index] = false;
          return;
        }

        hasCoinTimeoutRef.current[index] = true;
        setCurrentCoinAvatarDegree((prev) => {
          if (!prev) return prev;
          const newDegree = [...prev];
          this.changeAvatar(index, newDegree[index]);
          newDegree[index] += increment;
          return newDegree;
        });
        coinMaskDegreeRef.current[index] += increment;
        const timer = setTimeout(() => {
          coinTimersRef.current.delete(timer);
          this.coreRotate(index);
        }, 1);
        coinTimersRef.current.add(timer);
      }
    }

    class socketHooks {
      static addNewBid(p: PostCardsProps) {
        const index = props.cards.findIndex((card) => card.id === p.id);
        if (index < 0 || !isCurrentBid(confirmedCardsRef.current[p.id], p, props.bidWindowStart))
          return;
        confirmedCardsRef.current = { ...confirmedCardsRef.current, [p.id]: p };
        setConfirmedCards(confirmedCardsRef.current);

        if (!lastBidOrgRef.current[index]) {
          setCoinOrgAvatar((prev) => {
            if (!prev) return prev;
            const newCoin = [...prev];
            newCoin[index] = { ...newCoin[index], oddOrgAvatar: p.orgAvatar || "" };
            return newCoin;
          });
        } else {
          coinAvatar.rotate(index, p.orgAvatar || "");
        }

        const remaining = remainingBidCooldown(p.lastBidDate);
        setWaitingBidTime((prev) => {
          const next = { ...prev };
          if (remaining) next[p.id] = remaining;
          else delete next[p.id];
          return next;
        });
        if (remaining) {
          countdownBid(p.id);
        } else {
          clearInterval(waitBidTimeoutRef.current[p.id]);
          delete waitBidTimeoutRef.current[p.id];
        }

        setLastBidOrg((prev) => {
          if (!prev) return prev;
          const newLastBidOrg = [...prev];
          newLastBidOrg[index] = p.lastBidOrg || "";
          return newLastBidOrg;
        });

        lastBidOrgRef.current[index] = p.lastBidOrg || "";
      }

      static openNewPost(postId: string) {
        if (!openedCardsRef.current?.includes(postId)) {
          openedCardsRef.current.push(postId);
        }

        setOpenedCards((prev) => {
          if (!prev) return prev;
          const newOpenedCards = [...prev];
          if (newOpenedCards.includes(postId)) return newOpenedCards;
          newOpenedCards.push(postId);
          return newOpenedCards;
        });
      }
    }

    const previewAmount =
      previewPostId && canBid && confirmedCards[previewPostId]?.lastBidOrg !== userInfo?.org_id
        ? bidCandidate(confirmedCards[previewPostId]?.lastBidAmount, props.selectedBid)
        : undefined;
    React.useEffect(() => {
      if (!previewPostId || previewAmount === undefined) {
        setTaxQuote(undefined);
        return;
      }
      const request = new AbortController();
      const identity = { postId: previewPostId, amount: previewAmount };
      const revision = snapshotModeRef.current;
      const get = negotiateAPI.get;
      setTaxQuote(identity);
      const timer = setTimeout(async () => {
        try {
          const JWT = (await localforage.getItem("JWT")) as string | undefined;
          if (request.signal.aborted || revision !== getNegotiationModeRevision()) return;
          if (!JWT) throw new Error("Session unavailable");
          const response = await get("/org-tax-preview", {
            headers: { "X-JWT": JWT },
            params: { value: previewAmount, postId: previewPostId },
            signal: request.signal,
            timeout: 10_000,
          });
          if (!request.signal.aborted && revision === getNegotiationModeRevision())
            setTaxQuote({
              ...identity,
              value: chargePreviewValue(response.data, previewAmount),
              balance: parseNegotiationBalancePreview(response.data?.negotiationBalance),
            });
        } catch {
          if (!request.signal.aborted) setTaxQuote({ ...identity, error: true });
        }
      }, 750);
      return () => {
        request.abort();
        clearTimeout(timer);
      };
      // Every confirmed bid can change the organization's monopoly multiplier,
      // including bids on another post while this preview remains open. A
      // moved Saldo de Negociações (quoteNonce) changes what the bid needs.
    }, [previewPostId, previewAmount, confirmedCards, quoteNonce]);

    // Turbo: this card's own quote says the Saldo de Negociações cannot admit
    // its quick bid, so the card reads as disabled. A click still explains
    // what is missing instead of sending the bid.
    function quoteBlocksBid(cardId: string): boolean {
      return (
        turboEnabled &&
        cardId === previewPostId &&
        previewAmount !== undefined &&
        taxQuote?.postId === cardId &&
        taxQuote.amount === previewAmount &&
        !pending.attempts[cardId] &&
        balanceShortfall(taxQuote.balance) !== null
      );
    }

    function renderTaxQuote(cardId: string, size: number) {
      if (cardId !== previewPostId || previewAmount === undefined) return <span>---</span>;
      const matching = taxQuote?.postId === cardId && taxQuote.amount === previewAmount;
      if (!matching || (taxQuote.value === undefined && !taxQuote.error))
        return <CircularProgress size={size} sx={{ color: "var(--neutral-11)" }} />;
      if (taxQuote.error) return <span className="quoteError">---</span>;
      const shortfall = balanceShortfall(taxQuote.balance);
      return (
        <span>
          R${formatBRL(taxQuote.value!)}
          {shortfall && (
            <span className="quoteBalanceShort">
              {" · "}
              {shortfall === "missing"
                ? "Saldo insuficiente"
                : shortfall === "restricted"
                  ? "Saldo bloqueado"
                  : "Saldo indisponível"}
            </span>
          )}
        </span>
      );
    }

    function definePostContainerHeight() {
      const listContainersIds: string[] = [
        "hallGeneralResume",
        "hallOptionsContainer",
        "hallChipsBetContainer",
        "negotiateNavbar",
        "autoBuyWarningNegotiate",
      ];

      let consumedHeight = 0;
      for (const containerId of listContainersIds) {
        const container = document.getElementById(containerId);
        if (!container) {
          console.error(`Container with ID ${containerId} not found.`);
          continue;
        }

        const containerStyles = getComputedStyle(container);
        const containerMarginTop = parseFloat(containerStyles.marginTop || "0");
        const containerMarginBottom = parseFloat(containerStyles.marginBottom || "0");
        consumedHeight += container.offsetHeight + containerMarginTop + containerMarginBottom;
      }

      const hallContainer = document.getElementById("postListContainer");
      if (!hallContainer) return;

      hallContainer.style.height = `calc(100vh - ${consumedHeight}px - 130px)`;
    }

    function getClassNameByHighlightCard(cardId: string) {
      // `highlightTablePositions` maps card ids onto the SHARED template objects
      // declared once in `highlightContainerClassNames`. resolveHighlightClassName
      // never mutates those entries (it clones before merging power-offensive
      // classes), so the templates can't accumulate classes across renders.
      //
      // buildHighlightPositionTable gives TOP precedence over flop: an object
      // literal with computed keys would let a later flop key overwrite an
      // earlier top key when the same id lands in both (which happens for small
      // boards), flipping the destaque card to `type:"flops"` so it disappears
      // from the default HITS filter. Here the top always wins.
      const highlightTablePositions = buildHighlightPositionTable<ClassHighlightTypes>(
        {
          top1Id: props.top1Id,
          top2Id: props.top2Id,
          top3Id: props.top3Id,
          flop1Id: props.flop1Id,
          flop2Id: props.flop2Id,
          flop3Id: props.flop3Id,
        },
        {
          top1: highlightContainerClassNames.top1,
          top2: highlightContainerClassNames.top2,
          top3: highlightContainerClassNames.top3,
          flop1: highlightContainerClassNames.flop1,
          flop2: highlightContainerClassNames.flop2,
          flop3: highlightContainerClassNames.flop3,
        },
      );

      return resolveHighlightClassName(
        cardId,
        highlightTablePositions,
        props.powerOffensiveIds,
        highlightContainerClassNames.powerOffensive,
      );
    }

    async function createLastBid(card: PostCardsProps) {
      // Per-card in-flight guard: turbo mode accepts repeated clicks, so
      // block a second POST for the same card until the first settles.
      if (
        !isNegotiationModeReady() ||
        snapshotModeRef.current !== getNegotiationModeRevision() ||
        !canBid ||
        pending.error ||
        !userInfoRef.current ||
        bidInFlightRef.current.has(card.id)
      )
        return false;

      const confirmed = confirmedCardsRef.current[card.id];
      const increment = props.selectedBid;
      const pendingAttempt = pending.attempts[card.id];
      const amount =
        pendingAttempt?.payload.lastBidAmount ?? bidCandidate(confirmed?.lastBidAmount, increment);
      if (
        amount === undefined ||
        !confirmed ||
        (!pendingAttempt &&
          (confirmed.lastBidOrg === userInfoRef.current?.org_id ||
            remainingBidCooldown(confirmed.lastBidDate) > 0))
      )
        return false;
      // The Saldo de Negociações cannot admit this bid: show what is missing
      // instead of sending it. The quote of this exact bid decides when there
      // is one; otherwise the last balance answer is a coarse gate (a
      // restricted account, or less available than the bid's own value).
      // Payments still decides every bid that goes out. A frozen attempt is
      // always replayed: its key never reserves anything new.
      if (!pendingAttempt) {
        const quote =
          taxQuote?.postId === card.id && taxQuote.amount === amount && taxQuote.value !== undefined
            ? taxQuote
            : undefined;
        if (quote) {
          if (quote.balance && balanceShortfall(quote.balance) !== null) {
            setBalanceNotice({ preview: quote.balance, atLeast: false });
            return false;
          }
        } else {
          const coarse = coarseBalanceShortfall(balanceActions?.prepaidSnapshot() ?? null, amount);
          if (coarse) {
            setBalanceNotice({ preview: coarse, atLeast: true });
            return false;
          }
        }
      }
      bidInFlightRef.current.add(card.id);
      setSendingBidIds((previous) => new Set(previous).add(card.id));
      const request = new AbortController();
      const session = { ...userInfoRef.current };
      const revision = snapshotModeRef.current;
      const context = captureBidContext(session, card.id);
      bidRequestsRef.current.add(request);
      try {
        const JWT = (await localforage.getItem("JWT")) as string | undefined;
        if (
          request.signal.aborted ||
          !isNegotiationModeReady() ||
          revision !== getNegotiationModeRevision()
        )
          return false;
        if (!JWT) throw new Error("Session unavailable");
        await submitBidAttempt(
          session,
          {
            postId: card.id,
            lastBidAmount: amount,
            lastBidDiff: increment,
          },
          {
            params: { bandwidth: bandwidthMbps },
            headers: { "X-JWT": JWT },
            signal: request.signal,
            timeout: 15_000,
          },
          () => {},
          { context, expectedAttempt: pendingAttempt },
        );
        props.onBidLanded?.();
        setBalanceNotice(undefined);
        balanceActions?.refresh();
      } catch (error) {
        if (request.signal.aborted) return false;
        if (isRecordedBidRefusal(error)) props.onBidLanded?.();
        // Every answer can move the balance; a balance refusal also re-quotes.
        balanceActions?.refresh();
        if (negotiationBalanceRefusal(error instanceof PendingBidError ? error.original : error))
          setQuoteNonce((nonce) => nonce + 1);
        setBidError(
          error instanceof PendingBidError ||
            error instanceof BidCreationUnavailableError ||
            error instanceof StaleBidAttemptError
            ? error.message
            : getUserFacingError(error, {
                context: "bid",
                fallback:
                  "Não foi possível confirmar seu lance. Confira o lance atual antes de tentar novamente.",
              }),
        );
        return false;
      } finally {
        bidRequestsRef.current.delete(request);
        bidInFlightRef.current.delete(card.id);
        setSendingBidIds((previous) => {
          const next = new Set(previous);
          next.delete(card.id);
          return next;
        });
      }
    }

    async function getOpenedCards() {
      const lifecycle = lifecycleRef.current;
      const revision = snapshotModeRef.current;
      const get = negotiateAPI.get;
      const request = new AbortController();
      requestControllersRef.current.add(request);
      try {
        const JWT = (await localforage.getItem("JWT")) as string | undefined;
        if (
          !JWT ||
          request.signal.aborted ||
          lifecycle !== lifecycleRef.current ||
          revision !== getNegotiationModeRevision()
        )
          return false;
        const response = await get(`/opened`, {
          headers: { "X-JWT": JWT },
          signal: request.signal,
          timeout: 10_000,
        });
        if (
          request.signal.aborted ||
          lifecycle !== lifecycleRef.current ||
          revision !== getNegotiationModeRevision()
        )
          return false;
        const opened = Array.from(new Set([...openedCardsRef.current, ...response.data]));
        setOpenedCards(opened);
        openedCardsRef.current = opened;
      } catch (error) {
        if (request.signal.aborted || lifecycle !== lifecycleRef.current) return false;
        console.error("Error fetching opened cards", error);
        return false;
      } finally {
        requestControllersRef.current.delete(request);
      }
    }

    async function backendOpenSelectedPost(card: PostCardsProps) {
      if (
        !isNegotiationModeReady() ||
        snapshotModeRef.current !== getNegotiationModeRevision() ||
        openedCardsRef.current.includes(card.id) ||
        openInFlightRef.current.has(card.id)
      )
        return;
      openInFlightRef.current.add(card.id);
      const lifecycle = lifecycleRef.current;
      const revision = snapshotModeRef.current;
      const patch = negotiateAPI.patch;
      const request = new AbortController();
      requestControllersRef.current.add(request);
      try {
        const JWT = (await localforage.getItem("JWT")) as string | undefined;
        if (
          !JWT ||
          request.signal.aborted ||
          lifecycle !== lifecycleRef.current ||
          revision !== getNegotiationModeRevision()
        )
          return false;
        await patch(
          `/open/${card.id}`,
          {},
          {
            headers: { "X-JWT": JWT },
            signal: request.signal,
            timeout: 10_000,
          },
        );
        if (
          !request.signal.aborted &&
          lifecycle === lifecycleRef.current &&
          revision === getNegotiationModeRevision()
        )
          socketHooks.openNewPost(card.id);
      } catch (error) {
        if (request.signal.aborted || lifecycle !== lifecycleRef.current) return false;
        setBidError(
          "Não foi possível registrar a leitura. Abra a publicação novamente antes de usar o modo turbo.",
        );
        return false;
      } finally {
        requestControllersRef.current.delete(request);
        openInFlightRef.current.delete(card.id);
      }
    }

    function countdownBid(postId: string) {
      if (waitBidTimeoutRef.current[postId]) return;
      waitBidTimeoutRef.current[postId] = setInterval(() => {
        setWaitingBidTime((prev) => {
          if (!prev) return prev;
          const newWaitingBidTime = { ...prev };
          if ((newWaitingBidTime[postId] || 0) <= 1) {
            clearInterval(waitBidTimeoutRef.current[postId]);
            delete waitBidTimeoutRef.current[postId];
            delete newWaitingBidTime[postId];
            return newWaitingBidTime;
          }

          newWaitingBidTime[postId] = newWaitingBidTime[postId] - 1;
          return newWaitingBidTime;
        });
      }, 1010);
    }

    function startInitialCountdownIfNecessary(cards = props.cards) {
      const newWaitingBidTime: { [key: string]: number } = {};
      cards.forEach((card) => {
        const remaining = remainingBidCooldown(card.lastBidDate);
        if (remaining) newWaitingBidTime[card.id] = remaining;
      });

      setWaitingBidTime(newWaitingBidTime);
      for (const postId in newWaitingBidTime) {
        countdownBid(postId);
      }
    }

    function openSelectedPostOrBid(card: PostCardsProps, _index: number) {
      if (!isNegotiationModeReady() || snapshotModeRef.current !== getNegotiationModeRevision())
        return;
      // Turbo lists only posts the organization already analysed, and the
      // server does not require an open post to accept a bid: a turbo click
      // bids directly, no extra client-side gate.
      if (turboEnabled) {
        if (pending.attempts[card.id]) {
          setSelectedPost({ ...card });
          return;
        }
        createLastBid(card);
        return;
      }

      const confirmed = confirmedCards[card.id] ?? card;
      const candidate = bidCandidate(confirmed.lastBidAmount, props.selectedBid);
      if (candidate === undefined) return;
      setSelectedPost({
        id: card.id,
        username: card.username,
        position: card.position,
        lastBidAmount: candidate,
        lastBidOrg: confirmed.lastBidOrg,
        avatar: card.avatar,
        orgTax: confirmed.orgTax,
        lastBidDate: confirmed.lastBidDate,
      });
      backendOpenSelectedPost(card);
    }

    async function initialize() {
      const lifecycle = lifecycleRef.current;
      const initialization = ++initializationRef.current;
      // Initialize synchronously before attaching live listeners. A delayed
      // session read must not overwrite a newer event or another board.
      const sameWindow = confirmedWindowStartRef.current === props.bidWindowStart;
      const initialCards = props.cards.map((card) => {
        const observed = confirmedCardsRef.current[card.id];
        if (sameWindow && observed && !isCurrentBid(observed, card))
          return {
            ...card,
            lastBidAmount: observed.lastBidAmount,
            lastBidDate: observed.lastBidDate,
            lastBidOrg: observed.lastBidOrg,
            orgTax: observed.orgTax,
            orgAvatar: observed.orgAvatar,
          };
        return card;
      });
      confirmedWindowStartRef.current = props.bidWindowStart;
      confirmedCardsRef.current = Object.fromEntries(initialCards.map((card) => [card.id, card]));
      setConfirmedCards(confirmedCardsRef.current);
      setSelectedPost((previous) => {
        if (!previous) return undefined;
        const confirmed = confirmedCardsRef.current[previous.id];
        if (!confirmed) return undefined;
        return {
          ...previous,
          ...confirmed,
          lastBidAmount:
            bidCandidate(confirmed.lastBidAmount, props.selectedBid) ?? confirmed.lastBidAmount,
        };
      });
      const userPromise = sessionInfo();
      setCurrentCoinAvatarDegree(initialCards.map(() => 0));
      setCoinOrgAvatar(
        initialCards.map((card) => ({
          oddOrgAvatar: card.orgAvatar || "",
          evenOrgAvatar: "",
        })),
      );
      setLastBidOrg(initialCards.map((card) => card.lastBidOrg || ""));
      startInitialCountdownIfNecessary(initialCards);
      getOpenedCards();
      coinFinalDegreeRef.current = props.cards.map(() => 0);
      coinMaskDegreeRef.current = props.cards.map(() => 0);
      hasCoinTimeoutRef.current = props.cards.map(() => false);
      finalCoinOrgAvatarRef.current = initialCards.map((card) => ({
        oddOrgAvatar: card.orgAvatar || "",
        evenOrgAvatar: "",
      }));
      lastBidOrgRef.current = initialCards.map((card) => card.lastBidOrg || "");
      const user = await userPromise;
      if (lifecycle === lifecycleRef.current && initialization === initializationRef.current) {
        userInfoRef.current = user;
        setUserInfo(user);
      }
    }

    const HighlightPostType: React.FC<{ type: string }> = (props) => {
      switch (props.type) {
        case "hits":
          return <StarRounded sx={{ fontSize: "24px" }} />;
        case "flops":
          return <TrendingDownRounded sx={{ fontSize: "24px" }} />;
        case "power-offensive":
          return (
            <div className="offensiveTopContainer">
              <div className="offensiveTopFire">
                <FireComponent className="smallFire" />
              </div>
            </div>
          );
        case "2+":
          return (
            <div className="offensiveTopContainer">
              <div className="offensiveTopFire">
                <FireComponent className="smallFire" />
              </div>
            </div>
          );
      }
    };

    React.useEffect(() => {
      definePostContainerHeight();
      window.addEventListener("resize", definePostContainerHeight);
      return () => {
        window.removeEventListener("resize", definePostContainerHeight);
      };
    });

    React.useEffect(() => {
      RTA.on("openNewPost", socketHooks.openNewPost);
      return () => {
        RTA.off("openNewPost", socketHooks.openNewPost);
      };
    }, [turboEnabled]);

    React.useEffect(() => {
      initialize();
      RTA.on("addNewBid", socketHooks.addNewBid);
      return () => {
        Object.values(waitBidTimeoutRef.current).forEach(clearInterval);
        waitBidTimeoutRef.current = {};
        coinTimersRef.current.forEach(clearTimeout);
        coinTimersRef.current.clear();
        RTA.off("addNewBid", socketHooks.addNewBid);
      };
    }, [props.cards, props.bidWindowStart]);

    if (!props.cards) return <></>;
    return (
      <>
        <div id="postListContainer">
          <span className="negotiateVisuallyHidden" role="status">
            {sendingBidIds.size > 0 ? "Enviando lance…" : ""}
          </span>
          <BannerFeedback
            type="error"
            open={Boolean(bidError)}
            message={bidError}
            callback={() => setBidError("")}
          />
          {balanceNotice && turboEnabled && (
            <div className="hallBalanceNotice" data-testid="turbo-balance-notice">
              <NegotiationShortfall
                preview={balanceNotice.preview}
                atLeast={balanceNotice.atLeast}
                onRecharge={
                  balanceActions?.canRecharge && canBid ? balanceActions.openRecharge : undefined
                }
              />
              <button
                type="button"
                className="hallBalanceNoticeClose"
                onClick={() => setBalanceNotice(undefined)}
              >
                Fechar aviso
              </button>
            </div>
          )}
          {turboEnabled && openedCards.length === 0 ? (
            <div id="emptyOptionPostsInTurboMode">
              <VisibilityOffRounded
                sx={{
                  fontSize: "50px",
                  color: "#fff",
                }}
              />
              <span>
                Nenhum post foi analisado pela sua organização. Clique em um post com o modo turbo
                desativado para analisar e habilitar lances no modo turbo.
              </span>
            </div>
          ) : (
            <>
              <div id="highlightPostsList">
                {props.cards.map((card, index) => {
                  if (turboEnabled) {
                    if (!openedCards.includes(card.id)) {
                      return <></>;
                    }
                  }

                  const classNames = getClassNameByHighlightCard(card.id);
                  if (!classNames) return <></>;
                  if (props.hallFilter === "hits" && classNames.type === "flops") return <></>;
                  if (props.hallFilter === "flops" && classNames.type === "hits") return <></>;
                  return (
                    <div
                      className={classNames.container}
                      id={`highlightCardPos${card.position}`}
                      key={card.id}
                    >
                      {turboEnabled && waitingBidTime[card.id] > 0 && (
                        <div className="turboModeCountdown">
                          <span>Espere:</span>
                          <span className="turboModeCountdownTime">{waitingBidTime[card.id]}s</span>
                        </div>
                      )}
                      {sendingBidIds.has(card.id) && (
                        <div className="turboModeCountdown turboModeSending" aria-hidden="true">
                          <CircularProgress color="inherit" size={28} aria-hidden="true" />
                          <span>Enviando lance…</span>
                        </div>
                      )}
                      <div className="highlightBackground" />
                      <div
                        className={classNames.post}
                        role="button"
                        tabIndex={0}
                        aria-disabled={quoteBlocksBid(card.id) || undefined}
                        data-balance-blocked={quoteBlocksBid(card.id) ? "true" : undefined}
                        aria-label={`${turboEnabled ? "Dar lance na" : "Analisar"} publicação de ${card.username}`}
                        {...previewFocusHandlers(card.id)}
                        onKeyDown={(event) => {
                          pointerFocusRef.current = false;
                          setFocusedPostId(card.id);
                          if (event.key !== "Enter" && event.key !== " ") return;
                          event.preventDefault();
                          openSelectedPostOrBid(card, index);
                        }}
                        onClick={() => {
                          openSelectedPostOrBid(card, index);
                        }}
                        onMouseEnter={() => setHoveredPostId(card.id)}
                        onMouseLeave={() =>
                          setHoveredPostId((current) => (current === card.id ? undefined : current))
                        }
                      >
                        <div className={classNames.userInfo}>
                          <ResolvedAvatar
                            src={card.avatar}
                            alt={card.username}
                            sx={{ width: 90, height: 90 }}
                          />
                          <span>{card.username}</span>
                        </div>
                        <div className="highlightPriceGeneral">
                          <h1 aria-label={`R$${bidTargets[card.id]}`}>
                            <AnimatedBidValue value={bidTargets[card.id]} />
                            <span className="highlightIncreasePreviewIcon">
                              <KeyboardDoubleArrowUpRoundedIcon />
                            </span>
                          </h1>
                          <span className="highlightTotalWithTax">
                            Total:
                            {renderTaxQuote(card.id, 10)}
                          </span>
                        </div>
                      </div>
                      <div className={classNames.postPosition}>
                        <span># {card.position}</span>
                      </div>
                      <div className={classNames.reasonPosition}>
                        <HighlightPostType type={classNames.type} />
                      </div>
                      {coinOrgAvatar[index] && (
                        <div
                          className="avatarCoinContainer"
                          role="img"
                          aria-label={
                            lastBidOrg[index]
                              ? lastBidOrg[index] === userInfo?.org_id
                                ? "Sua empresa está vencendo esta publicação"
                                : "Outra empresa está vencendo esta publicação"
                              : "Publicação sem lances"
                          }
                          style={{
                            opacity: lastBidOrg[index] ? 1 : 0,
                          }}
                        >
                          <div
                            className="avatarCoinInner"
                            style={{
                              transform: `rotateY(${currentCoinAvatarDegree[index] * -1}deg)`,
                            }}
                          >
                            <div className="avatarCoinContent avatarCoinFront">
                              <ResolvedOrgAvatarImg
                                src={coinOrgAvatar[index].oddOrgAvatar}
                                alt=""
                                fallback={
                                  <div className="orgAvatarDefault">
                                    <GroupsRounded sx={{ fontSize: "30px" }} />
                                  </div>
                                }
                              />
                            </div>
                            <div className="avatarCoinContent avatarCoinBack">
                              <ResolvedOrgAvatarImg
                                src={coinOrgAvatar[index].evenOrgAvatar}
                                alt=""
                                fallback={
                                  <div className="orgAvatarDefault">
                                    <GroupsRounded sx={{ fontSize: "30px" }} />
                                  </div>
                                }
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div id="hallPostsLists">
                {props.hallFilter === "hits"
                  ? props.cards.map((card, index) => {
                      if (turboEnabled) {
                        if (!openedCards.includes(card.id)) {
                          return <></>;
                        }
                      }

                      let containerClassName = "hallPostItemContainer";
                      let contentClassName = "hallPostItem";
                      let positionClassName = "hallPostItemPosition";
                      let valueClassName = "hallPostItemValue";
                      const classNameHighlight = getClassNameByHighlightCard(card.id);
                      if (classNameHighlight) {
                        containerClassName = classNameHighlight.chipContainer;
                        contentClassName = classNameHighlight.chipContent;
                        positionClassName = classNameHighlight.chipPosition;
                        valueClassName = classNameHighlight.chipValue;
                      }
                      return (
                        <Chip
                          class={containerClassName}
                          id={`chipPos${card.position}`}
                          key={card.id}
                          onMouseEnter={() => setHoveredPostId(card.id)}
                          onMouseLeave={() =>
                            setHoveredPostId((current) =>
                              current === card.id ? undefined : current,
                            )
                          }
                        >
                          {turboEnabled && waitingBidTime[card.id] > 0 && (
                            <div
                              className="turboModeCountdownChip"
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                              }}
                            >
                              <span>
                                Espere:{" "}
                                <span className="turboModeCountdownTimeChip">
                                  {waitingBidTime[card.id]}s
                                </span>
                              </span>
                            </div>
                          )}
                          {sendingBidIds.has(card.id) && (
                            <div
                              className="turboModeCountdownChip turboModeSendingChip"
                              aria-hidden="true"
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                              }}
                            >
                              <span>
                                <CircularProgress color="inherit" size={16} />
                                Enviando…
                              </span>
                            </div>
                          )}
                          <div
                            className={contentClassName}
                            role="button"
                            tabIndex={0}
                            aria-disabled={quoteBlocksBid(card.id) || undefined}
                            data-balance-blocked={quoteBlocksBid(card.id) ? "true" : undefined}
                            aria-label={`${turboEnabled ? "Dar lance na" : "Analisar"} publicação #${card.position} de ${card.username}`}
                            {...previewFocusHandlers(card.id)}
                            onKeyDown={(event) => {
                              pointerFocusRef.current = false;
                              setFocusedPostId(card.id);
                              if (event.key !== "Enter" && event.key !== " ") return;
                              event.preventDefault();
                              openSelectedPostOrBid(card, index);
                            }}
                            onClick={() => openSelectedPostOrBid(card, index)}
                          >
                            <span className={positionClassName}>#{card.position}</span>
                            <div className="hallPostItemValueContainer">
                              <span
                                className={valueClassName}
                                aria-label={`R$${bidTargets[card.id]}`}
                              >
                                <AnimatedBidValue value={bidTargets[card.id]} />
                              </span>
                              <span className="hallPostItemIncreasePreviewIcon">
                                <KeyboardDoubleArrowUpRoundedIcon />
                              </span>
                            </div>
                          </div>
                          <span
                            className="hallPostItemTotalWithTax"
                            onClick={() => {
                              if (turboEnabled && waitingBidTime[card.id] > 0) return;
                              openSelectedPostOrBid(card, index);
                            }}
                          >
                            Total:
                            {renderTaxQuote(card.id, 8)}
                          </span>
                          {coinOrgAvatar[index] && (
                            <div
                              className="avatarCoinContainerChip"
                              role="img"
                              aria-label={
                                lastBidOrg[index]
                                  ? lastBidOrg[index] === userInfo?.org_id
                                    ? "Sua empresa está vencendo esta publicação"
                                    : "Outra empresa está vencendo esta publicação"
                                  : "Publicação sem lances"
                              }
                              onClick={() => {
                                if (turboEnabled && waitingBidTime[card.id] > 0) return;
                                openSelectedPostOrBid(card, index);
                              }}
                              style={{
                                opacity: lastBidOrg[index] ? 1 : 0,
                                // A hidden coin is not a target: its click opens or, in turbo, bids.
                                pointerEvents: lastBidOrg[index] ? undefined : "none",
                              }}
                            >
                              <div
                                className="avatarCoinInner"
                                style={{
                                  transform: `rotateY(${currentCoinAvatarDegree[index] * -1}deg)`,
                                }}
                              >
                                <div className="avatarCoinContent avatarCoinFront">
                                  <ResolvedOrgAvatarImg
                                    src={coinOrgAvatar[index].oddOrgAvatar}
                                    alt=""
                                    fallback={
                                      <div className="orgAvatarDefault">
                                        <GroupsRounded sx={{ fontSize: "30px" }} />
                                      </div>
                                    }
                                  />
                                </div>
                                <div className="avatarCoinContent avatarCoinBack">
                                  <ResolvedOrgAvatarImg
                                    src={coinOrgAvatar[index].evenOrgAvatar}
                                    alt=""
                                    fallback={
                                      <div className="orgAvatarDefault">
                                        <GroupsRounded sx={{ fontSize: "30px" }} />
                                      </div>
                                    }
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        </Chip>
                      );
                    })
                  : props.cards
                      .map((card, index) => {
                        if (turboEnabled) {
                          if (!openedCards.includes(card.id)) {
                            return <></>;
                          }
                        }

                        let containerClassName = "hallPostItemContainer";
                        let contentClassName = "hallPostItem";
                        let positionClassName = "hallPostItemPosition";
                        let valueClassName = "hallPostItemValue";
                        const classNameHighlight = getClassNameByHighlightCard(card.id);
                        if (classNameHighlight) {
                          containerClassName = classNameHighlight.chipContainer;
                          contentClassName = classNameHighlight.chipContent;
                          positionClassName = classNameHighlight.chipPosition;
                          valueClassName = classNameHighlight.chipValue;
                        }
                        return (
                          <Chip
                            class={containerClassName}
                            id={`chipPos${card.position}`}
                            key={card.id}
                            onMouseEnter={() => setHoveredPostId(card.id)}
                            onMouseLeave={() =>
                              setHoveredPostId((current) =>
                                current === card.id ? undefined : current,
                              )
                            }
                          >
                            {turboEnabled && waitingBidTime[card.id] > 0 && (
                              <div
                                className="turboModeCountdownChip"
                                onClick={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                }}
                              >
                                <span>
                                  Espere:{" "}
                                  <span className="turboModeCountdownTimeChip">
                                    {waitingBidTime[card.id]}s
                                  </span>
                                </span>
                              </div>
                            )}

                            {sendingBidIds.has(card.id) && (
                              <div
                                className="turboModeCountdownChip turboModeSendingChip"
                                aria-hidden="true"
                                onClick={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                }}
                              >
                                <span>
                                  <CircularProgress color="inherit" size={16} />
                                  Enviando…
                                </span>
                              </div>
                            )}
                            <div
                              className={contentClassName}
                              role="button"
                              tabIndex={0}
                              aria-disabled={quoteBlocksBid(card.id) || undefined}
                              data-balance-blocked={quoteBlocksBid(card.id) ? "true" : undefined}
                              aria-label={`${turboEnabled ? "Dar lance na" : "Analisar"} publicação #${card.position} de ${card.username}`}
                              {...previewFocusHandlers(card.id)}
                              onKeyDown={(event) => {
                                pointerFocusRef.current = false;
                                setFocusedPostId(card.id);
                                if (event.key !== "Enter" && event.key !== " ") return;
                                event.preventDefault();
                                openSelectedPostOrBid(card, index);
                              }}
                              onClick={() => openSelectedPostOrBid(card, index)}
                            >
                              <span className={positionClassName}>#{card.position}</span>
                              <div className="hallPostItemValueContainer">
                                <span
                                  className={valueClassName}
                                  aria-label={`R$${bidTargets[card.id]}`}
                                >
                                  <AnimatedBidValue value={bidTargets[card.id]} />
                                </span>
                                <span className="hallPostItemIncreasePreviewIcon">
                                  <KeyboardDoubleArrowUpRoundedIcon />
                                </span>
                              </div>
                            </div>
                            <span
                              className="hallPostItemTotalWithTax"
                              onClick={() => {
                                if (turboEnabled && waitingBidTime[card.id] > 0) return;
                                openSelectedPostOrBid(card, index);
                              }}
                            >
                              Total:
                              {renderTaxQuote(card.id, 8)}
                            </span>
                            {coinOrgAvatar[index] && (
                              <div
                                className="avatarCoinContainerChip"
                                role="img"
                                aria-label={
                                  lastBidOrg[index]
                                    ? lastBidOrg[index] === userInfo?.org_id
                                      ? "Sua empresa está vencendo esta publicação"
                                      : "Outra empresa está vencendo esta publicação"
                                    : "Publicação sem lances"
                                }
                                onClick={() => {
                                  if (turboEnabled && waitingBidTime[card.id] > 0) return;
                                  openSelectedPostOrBid(card, index);
                                }}
                                style={{
                                  opacity: lastBidOrg[index] ? 1 : 0,
                                  // A hidden coin is not a target: its click opens or, in turbo, bids.
                                  pointerEvents: lastBidOrg[index] ? undefined : "none",
                                }}
                              >
                                <div
                                  className="avatarCoinInner"
                                  style={{
                                    transform: `rotateY(${currentCoinAvatarDegree[index] * -1}deg)`,
                                  }}
                                >
                                  <div className="avatarCoinContent avatarCoinFront">
                                    <ResolvedOrgAvatarImg
                                      src={coinOrgAvatar[index].oddOrgAvatar}
                                      alt=""
                                      fallback={
                                        <div className="orgAvatarDefault">
                                          <GroupsRounded sx={{ fontSize: "30px" }} />
                                        </div>
                                      }
                                    />
                                  </div>
                                  <div className="avatarCoinContent avatarCoinBack">
                                    <ResolvedOrgAvatarImg
                                      src={coinOrgAvatar[index].evenOrgAvatar}
                                      alt=""
                                      fallback={
                                        <div className="orgAvatarDefault">
                                          <GroupsRounded sx={{ fontSize: "30px" }} />
                                        </div>
                                      }
                                    />
                                  </div>
                                </div>
                              </div>
                            )}
                          </Chip>
                        );
                      })
                      .reverse()}
              </div>
            </>
          )}
        </div>
        <PostDetails
          username={selectedPost?.username || ""}
          id={selectedPost?.id || ""}
          position={selectedPost?.position || 0}
          lastBidAmount={selectedCandidate ?? 0}
          lastBidOrg={selectedConfirmed?.lastBidOrg}
          orgAvatar={selectedConfirmed?.orgAvatar}
          avatar={selectedPost?.avatar}
          orgTax={selectedConfirmed?.orgTax || 0}
          lastBidDate={selectedConfirmed?.lastBidDate || ""}
          userInfo={userInfo}
          callbackClose={() => {
            clearPreview();
            setSelectedPost(undefined);
          }}
          lastBidDiff={props.selectedBid}
          bidWindowStart={props.bidWindowStart}
          onBidLanded={props.onBidLanded}
        />
      </>
    );
  },
  (previous, next) =>
    previous.cards === next.cards &&
    previous.hallFilter === next.hallFilter &&
    previous.selectedBid === next.selectedBid &&
    previous.turboMode === next.turboMode &&
    previous.bidWindowStart === next.bidWindowStart &&
    previous.powerOffensiveIds === next.powerOffensiveIds &&
    previous.top1Id === next.top1Id &&
    previous.top2Id === next.top2Id &&
    previous.top3Id === next.top3Id &&
    previous.flop1Id === next.flop1Id &&
    previous.flop2Id === next.flop2Id &&
    previous.flop3Id === next.flop3Id,
);

/** The chips every company gets. Ads refuses an increment below R$10 from all but DaKasa's own. */
export const BID_INCREMENTS: readonly number[] = [10, 25, 50, 100, 500];
/**
 * DaKasa's own company also bids in R$3 steps. This mirrors a server rule (Ads
 * ADR 0017): Ads accepts an increment from 3 to 9 only when the bidding
 * company's Payments billing CNPJ is DaKasa's, refuses anything below 3 from
 * everyone, and answers a recorded 409 `bid_increment_below_minimum`. The
 * console answer (`isDaKasaOrg`) only decides which chips to offer.
 */
export const DAKASA_BID_INCREMENTS: readonly number[] = [3, ...BID_INCREMENTS];
const DEFAULT_BID_INCREMENT = 25;
/** The example Saldo disponível of the tour, whose mock market has no balance. */
const TUTORIAL_AVAILABLE_CENTS = 50000;

export const BidIncrementChips: React.FC<{
  selected: number;
  onSelect: (value: number) => void;
  /** The chips offered, in order. Each stays a direct child of the row: the tour spotlights them. */
  increments?: readonly number[];
}> = ({ increments = BID_INCREMENTS, ...props }) => {
  return (
    <div id="hallChipsBetContainer">
      {increments.map((increment) => (
        <button
          key={increment}
          type="button"
          aria-pressed={props.selected === increment}
          className={
            props.selected === increment ? "hallChipsBetButtonSelected" : "hallChipsBetButton"
          }
          onClick={() => props.onSelect(increment)}
        >
          <span>{increment}</span>
        </button>
      ))}
    </div>
  );
};

/**
 * The offensive-points preview in the Átrio header bar.
 *
 * All three branches carry `id="offensivePreviewContainer"` — they are mutually
 * exclusive returns of a component rendered once, so there is never a duplicate
 * id. The id is the anchor the guided tour spotlights (courses.tsx), and it used
 * to exist only on the `base <= 0` branch: as soon as the organisation had a
 * winning bid the element the tutorial points at vanished, the spotlight
 * collapsed to 0×0 and the step highlighted nothing at all.
 */
export const PreviewOffensivePoints: React.FC<{ base: number; multiplier: number }> = (props) => {
  if (props.base <= 0)
    return (
      <div className="offensivePreviewContainer" id="offensivePreviewContainer">
        <WarningAmberRounded sx={{ fontSize: "28px", color: "#FFF" }} />
        <span>Risco de perda de ofensiva...</span>
        <InfoPopup
          position="s"
          id="offensivePreviewInfoPopup"
          classNameBox="offensivePreviewInfoPopupBox"
          withArrow
          triggerElement={
            <div className="offensivePreviewInfoPopupContainer">
              <InfoRounded sx={{ fontSize: "24px", color: "#FFF", opacity: "0.5" }} />
            </div>
          }
        >
          <span>Dê um lance em um post para ganhar ao menos uma ofensiva.</span>
        </InfoPopup>
      </div>
    );

  if (props.multiplier > 1)
    return (
      <div className="offensivePreviewContainerMultiplier" id="offensivePreviewContainer">
        <div className="offensivePreviewFireMultiplier">
          <span className="offensivePreviewFireBase">+{props.base}</span>
          <span className="offensivePreviewFireMultiplierSeparator">X</span>
          <span className="offensivePreviewFireMultiplierValue">{props.multiplier}</span>
        </div>
        <FireComponent className="smallFire multiplierFire" />
      </div>
    );

  return (
    <div className="offensivePreviewContainerOnlyBase" id="offensivePreviewContainer">
      <span className="offensivePreviewFireBase">+{props.base}</span>
      <FireComponent className="smallFire multiplierFire" />
    </div>
  );
};

export const NegotiateHome: React.FC = () => {
  const modeRevision = React.useSyncExternalStore(
    onNegotiationModeChange,
    getNegotiationModeRevision,
  );
  const [loadedModeRevision, setLoadedModeRevision] = React.useState<number>();
  const boardModeRevisionRef = React.useRef<number>();
  const orgModeRevisionRef = React.useRef<number>();
  const now = new Date();
  const [fakeLoading, setFakeLoading] = React.useState(true);
  const [hallFilter, setHallFilter] = React.useState<"hits" | "flops">("hits");
  const [bidSelected, setBidSelected] = React.useState<number>(DEFAULT_BID_INCREMENT);
  // `?? {}`: some unit renders mount the page without a UserProvider.
  const { isDaKasaOrg = false } = useUser() ?? {};
  const bidIncrements = isDaKasaOrg ? DAKASA_BID_INCREMENTS : BID_INCREMENTS;
  React.useEffect(() => {
    // A chip no longer offered (the DaKasa answer changed) never stays selected.
    if (!bidIncrements.includes(bidSelected)) setBidSelected(DEFAULT_BID_INCREMENT);
  }, [bidIncrements, bidSelected]);
  const [hallPosts, setHallPosts] = React.useState<HallOfCardsSnapshot<PostCardsProps>>();
  // HallOfCards keeps the first callback it gets (its memo compares data props
  // only), so hand it a stable one that always reaches the current refetch.
  const refetchBoardRef = React.useRef(refetchBoardInPlace);
  refetchBoardRef.current = refetchBoardInPlace;
  const onBidLanded = React.useCallback(() => refetchBoardRef.current(), []);
  React.useEffect(() => {
    window.addEventListener(BID_ATTEMPT_RESOLVED_EVENT, onBidLanded);
    return () => window.removeEventListener(BID_ATTEMPT_RESOLVED_EVENT, onBidLanded);
  }, [onBidLanded]);
  const [boardError, setBoardError] = React.useState("");
  const [openConfig, setOpenConfig] = React.useState<boolean>(false);
  const [totalBidsAmount, setTotalBidsAmount] = React.useState<{
    [key: string]: { amount: number; tax: number; transferFeeCents?: number };
  }>({});
  const [turboMode, setTurboMode] = React.useState(false);
  const [marketPeriod, setMarketPeriod] = React.useState(false);
  const [endNegotiation, setEndNegotiation] = React.useState<Date>();
  const [orgStatus, setOrgStatus] = React.useState<OrgStats>();
  const [offensivePreview, setOffensivePreview] = React.useState<{
    base: number;
    multiplier: number;
  }>({ base: 0, multiplier: 1 });
  const [getTime, setTime] = React.useState<Date>(now);
  const [bandwidthMbps] = React.useState(() => getBandwidthMbps());

  // Deadline (as epoch ms) for which the "the countdown just hit zero" resync
  // has already been fired, so the crossing triggers exactly ONE extra read of
  // `/negotiate-time` instead of one per tick of the 1 s clock.
  const marketResyncedForDeadlineRef = React.useRef<number | undefined>(undefined);
  const hallPostsRef = React.useRef<HallOfCardsSnapshot<PostCardsProps> | undefined>(undefined);
  const periodWindowRef = React.useRef<string>();
  const bidWindowStartRef = React.useRef<number>();
  const [autoBuyPending, setAutoBuyPending] = React.useState(false);
  const [autoBuyError, setAutoBuyError] = React.useState("");
  const autoBuyRequestRef = React.useRef<AbortController>();
  const readRequestsRef = React.useRef<Record<string, AbortController>>({});
  const loadingTimerRef = React.useRef<ReturnType<typeof setTimeout>>();
  const aliveRef = React.useRef(true);
  function beginRead(key: string) {
    readRequestsRef.current[key]?.abort();
    const request = new AbortController();
    readRequestsRef.current[key] = request;
    return request;
  }
  const userInfoRef = React.useRef<{ iss: string; org_id: string; exp: number } | undefined>(
    undefined,
  );
  const activeOrgRefOffensivePoint = React.useRef<number>(0);
  const { permissions } = usePermissions();
  const canBid = checkPermission(permissions.bid, "create");
  // Saldo de Negociações (prepaid Hall rail). Server-declared: nothing shows
  // unless Payments answers enabled with an account on the prepaid rail. The
  // tutorial (mock) market never reads it.
  const negotiationBalance = useNegotiationBalance({
    enabled: !isMockNegotiation(),
    resetKey: modeRevision,
  });
  const negotiationBalanceRef = React.useRef(negotiationBalance);
  negotiationBalanceRef.current = negotiationBalance;
  const [recharge, setRecharge] = React.useState<{ amountBRL?: number } | null>(null);
  const balanceListenersRef = React.useRef(new Set<() => void>());
  const prepaidBalance =
    hasNegotiationAccount(negotiationBalance.snapshot) &&
    negotiationBalance.snapshot.rail === "prepaid"
      ? negotiationBalance.snapshot
      : null;
  // A recharge opens from the auction only on a prepaid answer that accepts
  // top-ups; without one the RechargeFlow below has nothing to run on, and
  // a shortcut would look dead.
  const canRechargeFromAuction = Boolean(
    checkPermission(permissions.orgPayments, "create") &&
    prepaidBalance &&
    prepaidBalance.topupsEnabled &&
    prepaidBalance.account.state !== "closed",
  );
  const canRechargeRef = React.useRef(canRechargeFromAuction);
  canRechargeRef.current = canRechargeFromAuction;
  const balanceActions = React.useMemo<NegotiationBalanceActions>(
    () => ({
      refresh: () => negotiationBalanceRef.current.refresh(),
      prepaidSnapshot: () => {
        const current = negotiationBalanceRef.current;
        return !current.stale &&
          hasNegotiationAccount(current.snapshot) &&
          current.snapshot.rail === "prepaid"
          ? current.snapshot
          : null;
      },
      canRecharge: canRechargeFromAuction,
      openRecharge: (amountBRL) => {
        if (!canRechargeRef.current) return;
        setRecharge({ amountBRL: amountBRL && amountBRL > 0 ? amountBRL : undefined });
      },
      subscribe: (listener) => {
        balanceListenersRef.current.add(listener);
        return () => {
          balanceListenersRef.current.delete(listener);
        };
      },
    }),
    [canRechargeFromAuction],
  );
  // A recharge asked for runs on the prepaid answer. When that answer goes
  // away, drop the request too, so the modal never opens later unprompted.
  const hasPrepaidBalance = prepaidBalance !== null;
  React.useEffect(() => {
    if (!hasPrepaidBalance) setRecharge(null);
  }, [hasPrepaidBalance]);
  const subscribeToBalance = negotiationBalance.subscribe;
  React.useEffect(
    () => subscribeToBalance(() => balanceListenersRef.current.forEach((listener) => listener())),
    [subscribeToBalance],
  );
  const prepaidCycle = orgStatus?.settlementMode === "prepaid";
  const competitionExcluded = isCompetitionExcluded(orgStatus?.competitionExcluded, isDaKasaOrg);
  // The Saldo de Negociações beside the total. The tour's mock market never
  // reads Payments, so it shows a fixed example amount and no recharge; the
  // first read holds the slot so the row does not shift when it answers.
  const balanceSlot = prepaidBalance ? (
    <BalanceChip
      availableCents={prepaidBalance.availableCents}
      pendingTopupCents={prepaidBalance.pendingTopupCents}
      restricted={prepaidBalance.account.state === "restricted"}
      onRecharge={balanceActions.canRecharge ? () => balanceActions.openRecharge() : undefined}
    />
  ) : isMockNegotiation() ? (
    <BalanceChip availableCents={TUTORIAL_AVAILABLE_CENTS} pendingTopupCents={0} />
  ) : !negotiationBalance.loaded ? (
    <BalanceChip availableCents={null} pendingTopupCents={0} />
  ) : null;
  const notices = useNegotiationNotices(prepaidCycle, modeRevision, periodWindowRef.current);
  React.useEffect(() => {
    notices.refresh();
  }, [negotiationBalance.snapshot?.revision, notices.refresh]);
  class socketHooks {
    static addNewBid(p: PostCardsProps) {
      if (
        !isNegotiationModeReady() ||
        boardModeRevisionRef.current !== getNegotiationModeRevision()
      )
        return;
      // An empty board arrives as `cards: null`; a broadcast bid has no card here.
      if (!hallPostsRef.current?.cards) return;
      if (!userInfoRef.current) return;

      const index = hallPostsRef.current.cards.findIndex((card) => card.id === p.id);
      if (index < 0) return;
      if (!isCurrentBid(hallPostsRef.current.cards[index], p, bidWindowStartRef.current)) return;

      // Live HallPost events zero username/position/avatar. Merge only the bid
      // fields into a new snapshot so totals, dialog state and the winning
      // company share the same confirmation without mutating the child's props.
      const cards = [...hallPostsRef.current.cards];
      cards[index] = {
        ...cards[index],
        lastBidOrg: p.lastBidOrg,
        transferFeeCents: p.transferFeeCents,
        lastBidAmount: p.lastBidAmount,
        lastBidDate: p.lastBidDate,
        lastBidDiff: p.lastBidDiff,
        orgTax: p.orgTax,
        orgAvatar: p.orgAvatar || "",
      };
      hallPostsRef.current = { ...hallPostsRef.current, cards };
      calculatePreviewOffensivePoints();
      if (p.lastBidOrg === userInfoRef.current.org_id) {
        setTotalBidsAmount((prev) => {
          if (!prev) return prev;
          const newTotalBids = { ...prev };
          newTotalBids[p.id] = {
            amount: p.lastBidAmount,
            tax: p.orgTax,
            transferFeeCents: p.transferFeeCents,
          };
          return newTotalBids;
        });
      } else {
        setTotalBidsAmount((prev) => {
          if (!prev) return prev;
          const newTotalBids = { ...prev };
          delete newTotalBids[p.id];
          return newTotalBids;
        });
      }
    }

    static startNegotiateProcess() {
      // The push has no revision. Both push and polling converge through the
      // same authoritative period read, which cancels an older read first.
      void countDownToFinishNegotiation();
    }

    static changeAutoBuyStatus() {
      // The event has no revision. Read the current value instead of adopting
      // a delayed toggle after a newer change. An active write reads it on settle.
      if (!autoBuyRequestRef.current) void getOrgStats();
    }
  }

  function calculatePreviewOffensivePoints() {
    let currBase = 0;
    let multiplier = 1;

    if (!hallPostsRef.current?.cards) return;
    hallPostsRef.current?.cards.forEach((post) => {
      if (post.lastBidOrg === userInfoRef.current?.org_id) {
        activeOrgRefOffensivePoint.current = 1;
        currBase += 1;
        if (hallPostsRef.current?.powerOffensiveIds.includes(post.id)) {
          multiplier += 1;
        }
      }
    });

    currBase += activeOrgRefOffensivePoint.current;
    setOffensivePreview({ base: currBase, multiplier: multiplier });
  }

  async function countDownToFinishNegotiation() {
    if (!isNegotiationModeReady()) return false;
    const revision = getNegotiationModeRevision();
    const get = negotiateAPI.get;
    const request = beginRead("period");
    const JWT = (await localforage.getItem("JWT")) as string | undefined;
    if (
      !JWT ||
      request.signal.aborted ||
      !aliveRef.current ||
      revision !== getNegotiationModeRevision()
    )
      return false;

    const headers: { [key: string]: string | undefined } = {
      "X-JWT": JWT,
    };

    try {
      const response = await get("/negotiate-time", {
        headers: headers,
        signal: request.signal,
        timeout: 10_000,
      });
      if (request.signal.aborted || !aliveRef.current || revision !== getNegotiationModeRevision())
        return false;

      const data = response.data;
      if (!data) return false;

      // Reject an implausible deadline instead of adopting it: a missing
      // `end_at` in the Redis period hash comes back as the year-1 sentinel
      // "0001-01-01T00:00:00Z", which is a VALID Date and would otherwise be
      // read as a window that has already elapsed.
      const parsedEndAt = parseMarketEndAt(data.endAt);
      if (!parsedEndAt) {
        console.error("Ignoring implausible market endAt from /negotiate-time:", data.endAt);
      }

      if (parsedEndAt) {
        const parsedStartAt = parseMarketEndAt(data.startAt);
        const window = JSON.stringify([
          data.period,
          parsedStartAt?.toISOString(),
          parsedEndAt.toISOString(),
        ]);
        if (periodWindowRef.current !== window) {
          periodWindowRef.current = window;
          bidWindowStartRef.current = parsedStartAt?.getTime();
          activeOrgRefOffensivePoint.current = 0;
          setTurboMode(false);
          setTotalBidsAmount({});
          setOffensivePreview({ base: 0, multiplier: 1 });
          resyncBoard();
          void getOrgStats();
        }
      }

      setMarketPeriod(data.period === "negotiate");
      setEndNegotiation(parsedEndAt);
    } catch (error) {
      if (request.signal.aborted || !aliveRef.current) return false;
      console.error("Error fetching period:", error);
      return false;
    }
  }

  async function getOrgStats(): Promise<OrgStats | false> {
    if (!isNegotiationModeReady()) return false;
    const revision = getNegotiationModeRevision();
    const get = negotiateAPI.get;
    const request = beginRead("org");
    const JWT = (await localforage.getItem("JWT")) as string | undefined;
    if (
      !JWT ||
      request.signal.aborted ||
      !aliveRef.current ||
      revision !== getNegotiationModeRevision()
    )
      return false;

    const headers: { [key: string]: string | undefined } = {
      "X-JWT": JWT,
    };

    try {
      const res = await get(`/org-status`, {
        headers: headers,
        signal: request.signal,
        timeout: 10_000,
      });
      if (request.signal.aborted || !aliveRef.current || revision !== getNegotiationModeRevision())
        return false;
      const payload = res.data;
      if (!payload) return false;

      setOrgStatus(payload);
      orgModeRevisionRef.current = revision;
      activeOrgRefOffensivePoint.current = payload.activeOrg ? 1 : 0;
      calculatePreviewOffensivePoints();
      return payload;
    } catch (error) {
      if (request.signal.aborted || !aliveRef.current) return false;
      console.error("Error fetching org status", error);
      return false;
    }
  }

  function grossBidAmount() {
    if (!totalBidsAmount) return 0;
    return Object.values(totalBidsAmount).reduce((acc, card) => acc + card.amount, 0);
  }

  function totalAmount() {
    if (!totalBidsAmount) return 0;
    const discountPercent = orgStatus?.bidDiscountPercent || 0;
    const cents = Object.values(totalBidsAmount).reduce((acc, card) => {
      // The bid shown on every card stays gross. Settlement applies the prize
      // to that base and only then applies the effective multiplier. Rewarded
      // orgs already receive tax=1 from the bid service, but the explicit flag
      // makes this estimate safe while a board is resynchronizing.
      const multiplier = orgStatus?.monopolyFeeWaived ? 1 : card.tax;
      return (
        acc + card.amount * (100 - discountPercent) * multiplier + (card.transferFeeCents ?? 0)
      );
    }, 0);
    const minorTotal = notices.snapshot?.notices.find((notice) => notice.type === "minor_active");
    return (cents + (minorTotal?.type === "minor_active" ? minorTotal.totalCents : 0)) / 100;
  }

  async function getHallOfCards(force = false) {
    if (!isNegotiationModeReady()) return false;
    // The authoritative window read can clear this ref and refetch in the same
    // tick. React state may still contain the previous window until it renders.
    if (!force && !shouldFetchHallCards(hallPostsRef.current)) return;
    const windowAtRequest = periodWindowRef.current;
    const revision = getNegotiationModeRevision();
    const get = negotiateAPI.get;
    const request = beginRead("board");
    const JWT = (await localforage.getItem("JWT")) as string | undefined;
    if (
      !JWT ||
      request.signal.aborted ||
      !aliveRef.current ||
      revision !== getNegotiationModeRevision()
    )
      return false;

    const headers: { [key: string]: string | undefined } = {
      "X-JWT": JWT,
    };

    const userInfo = await sessionInfo();
    if (
      !userInfo ||
      request.signal.aborted ||
      !aliveRef.current ||
      revision !== getNegotiationModeRevision()
    )
      return false;
    userInfoRef.current = userInfo;
    RTA.emit("tagUserOrg", userInfo.org_id);
    try {
      const response = await get<HallPostsWire<PostCardsProps>>("/hall-posts", {
        params: { bandwidth: bandwidthMbps },
        headers: headers,
        signal: request.signal,
        timeout: 10_000,
      });
      if (
        request.signal.aborted ||
        !aliveRef.current ||
        revision !== getNegotiationModeRevision() ||
        windowAtRequest !== periodWindowRef.current
      )
        return false;

      const observed = Object.fromEntries(
        (hallPostsRef.current?.cards ?? []).map((card) => [card.id, card]),
      );
      const payload = {
        ...response.data,
        cards: response.data.cards?.map((wire: HallCardWire<PostCardsProps>) => {
          const card = withUnbidDefaults<PostCardsProps>(wire);
          return observed[card.id] && !isCurrentBid(observed[card.id], card)
            ? observed[card.id]
            : card;
        }),
      };
      hallPostsRef.current = mapHallOfCards(payload);
      boardModeRevisionRef.current = revision;
      setLoadedModeRevision(revision);
      setBoardError("");
      setHallPosts(hallPostsRef.current);
      calculatePreviewOffensivePoints();
      setTotalBidsAmount(
        (payload.cards ?? [])
          .filter((card: any) => card.lastBidOrg === userInfo?.org_id)
          .reduce((acc: any, card: any) => {
            acc[card.id] = {
              amount: card.lastBidAmount,
              tax: card.orgTax,
              transferFeeCents: card.transferFeeCents,
            };
            return acc;
          }, {}),
      );

      clearTimeout(loadingTimerRef.current);
      loadingTimerRef.current = setTimeout(() => {
        setFakeLoading(false);
      }, 5000);
    } catch (error) {
      if (request.signal.aborted || !aliveRef.current) return false;
      console.error("Error fetching hall posts", error);
      setFakeLoading(false);
      setBoardError(
        getUserFacingError(error, {
          fallback: "Não foi possível atualizar as publicações. Tente carregar o Átrio novamente.",
        }),
      );
      return false;
    }
  }

  // Hard-reset the board (lances, placement, coin, highlight) and refetch it.
  // The ref MUST be cleared BEFORE getHallOfCards: its guard (shouldFetchHallCards)
  // reads the ref synchronously, so leaving it set would short-circuit the fetch.
  // Clearing the state too remounts HallOfCards, which reseeds every per-card
  // confirmed card and avatar state from the fresh
  // cards instead of carrying stale per-index values across the gap.
  function resyncBoard() {
    hallPostsRef.current = undefined;
    setHallPosts(undefined);
    getHallOfCards();
  }

  // Refetch the board without blanking what is on screen. Keep the current
  // record while reading so newer RTA bids can
  // be merged into the response. initialize() reseeds every per-card mirror on the
  // [props.cards] change, so no stale value survives and the operator never sees
  // a blank flash. This is the recovery for the frequent, user-facing paths (the
  // 30s poll and tab focus); the hard blanking resyncBoard stays for the rare
  // reconnect and the new-cycle transition.
  function refetchBoardInPlace() {
    getHallOfCards(true);
  }

  function displayRestantTime(metaDate: Date | undefined = endNegotiation): string {
    if (!metaDate) {
      return "";
    }

    // Drive the countdown off the ticking `getTime` state (advanced every second
    // by the interval in the mount effect), not the render-top `now` snapshot —
    // that keeps this in lockstep with the `diff` gate below and guarantees the
    // displayed time updates each tick instead of freezing.
    if (metaDate.getTime() < getTime.getTime()) {
      return "";
    }

    const diff = metaDate.getTime() - getTime.getTime();
    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);

    let h = hours.toString();
    let m = minutes.toString();
    let s = seconds.toString();
    if (hours < 10) h = `0${h}`;
    if (minutes < 10) m = `0${m}`;
    if (seconds < 10) s = `0${s}`;

    return `${h}:${m}:${s}`;
  }

  async function changeAutoBuyStatus(newValue: boolean) {
    const revision = getNegotiationModeRevision();
    if (
      !isNegotiationModeReady() ||
      boardModeRevisionRef.current !== revision ||
      orgModeRevisionRef.current !== revision ||
      autoBuyRequestRef.current ||
      !checkPermission(permissions.handleNegotiateConfigs, "edit")
    )
      return;
    const patch = negotiateAPI.patch;
    const request = new AbortController();
    autoBuyRequestRef.current = request;
    readRequestsRef.current.org?.abort();
    setAutoBuyPending(true);
    setAutoBuyError("");
    try {
      const JWT = (await localforage.getItem("JWT")) as string | undefined;
      if (request.signal.aborted || !aliveRef.current || revision !== getNegotiationModeRevision())
        return;
      if (!JWT) throw new Error("Session unavailable");
      const prepaid = orgStatus?.settlementMode === "prepaid";
      await patch(
        prepaid ? "/sponsor-minor-audience" : "/change-autobuy",
        prepaid
          ? { enabled: newValue, termsVersion: termosEnterpriseNegotiationBalanceVersion }
          : { autoBuy: newValue },
        {
          headers: { "X-JWT": JWT },
          signal: request.signal,
          timeout: 15_000,
        },
      );
      if (request.signal.aborted || !aliveRef.current || revision !== getNegotiationModeRevision())
        return;
      // Do not show a local toggle as persisted before the write succeeds.
      const confirmed = await getOrgStats();
      notices.refresh();
      if (!confirmed && !request.signal.aborted)
        setAutoBuyError(
          "A alteração foi enviada, mas ainda não foi possível confirmar o estado. Reabra as configurações para consultar novamente.",
        );
    } catch (error) {
      if (request.signal.aborted || !aliveRef.current) return;
      setAutoBuyError(
        getUserFacingError(error, {
          fallback:
            orgStatus?.settlementMode === "prepaid"
              ? "Não foi possível confirmar a configuração. O modo não pode mudar entre o fim do leilão e o resultado. Consulte novamente antes de tentar."
              : "Não foi possível confirmar a promoção automática. Confira o estado antes de tentar novamente.",
        }),
      );
      await getOrgStats();
    } finally {
      if (autoBuyRequestRef.current === request) {
        autoBuyRequestRef.current = undefined;
        if (aliveRef.current) setAutoBuyPending(false);
      }
    }
  }

  React.useEffect(() => {
    aliveRef.current = true;
    let disposed = false;
    // A mode change is a new data source, even if its market dates happen to
    // match. No candidate, reading mark, config or total crosses that boundary.
    hallPostsRef.current = undefined;
    periodWindowRef.current = undefined;
    bidWindowStartRef.current = undefined;
    marketResyncedForDeadlineRef.current = undefined;
    userInfoRef.current = undefined;
    boardModeRevisionRef.current = undefined;
    orgModeRevisionRef.current = undefined;
    activeOrgRefOffensivePoint.current = 0;
    autoBuyRequestRef.current = undefined;
    setLoadedModeRevision(undefined);
    setHallPosts(undefined);
    setOrgStatus(undefined);
    setTotalBidsAmount({});
    setOffensivePreview({ base: 0, multiplier: 1 });
    setBidSelected(DEFAULT_BID_INCREMENT);
    setTurboMode(false);
    setOpenConfig(false);
    setAutoBuyError("");
    setAutoBuyPending(false);
    setBoardError("");
    setMarketPeriod(false);
    setEndNegotiation(undefined);
    setFakeLoading(true);
    countDownToFinishNegotiation();
    getOrgStats();
    getHallOfCards();
    const interval = setInterval(() => {
      setTime(new Date());
    }, 1000);

    if (isNegotiationModeReady()) {
      void sessionInfo()
        .then((userInfo) => {
          if (!userInfo || disposed || modeRevision !== getNegotiationModeRevision()) return;
          userInfoRef.current = userInfo;
          RTA.on(`changeAutoBuyStatus-${userInfo.org_id}`, socketHooks.changeAutoBuyStatus);
        })
        .catch(() => {});
    }

    RTA.on("addNewBid", socketHooks.addNewBid);
    RTA.on("negotiateMarket", socketHooks.startNegotiateProcess);
    return () => {
      disposed = true;
      aliveRef.current = false;
      Object.values(readRequestsRef.current).forEach((request) => request.abort());
      autoBuyRequestRef.current?.abort();
      clearTimeout(loadingTimerRef.current);
      RTA.off("addNewBid", socketHooks.addNewBid);
      RTA.off("negotiateMarket", socketHooks.startNegotiateProcess);
      RTA.off(
        `changeAutoBuyStatus-${userInfoRef.current?.org_id}`,
        socketHooks.changeAutoBuyStatus,
      );
      clearInterval(interval);
    };
  }, [modeRevision]);

  // HTTP is the source of truth for the market window; the RTA push is only an
  // accelerator. Without this the page read `/negotiate-time` exactly once per
  // mount, so a single missed `negotiateMarket` event left an open tab pinned
  // to the previous window's `endAt` forever.
  React.useEffect(() => {
    const poll = setInterval(() => {
      void countDownToFinishNegotiation();
      // The window is not the only thing a missed event can freeze: a single
      // dropped `addNewBid` with the socket still alive (e.g. enterprise-rta
      // restarting from StartID="$") leaves lances, placement and highlight
      // stuck with no cure. The poll heals the board too, but only while the
      // tab is in focus, and it refetches in place so the operator never sees a
      // blank flash every 30 seconds during an open market.
      if (document.visibilityState === "visible") refetchBoardInPlace();
    }, MARKET_PERIOD_POLL_MS);

    // A backgrounded tab gets its timers throttled, so the poll alone can come
    // back arbitrarily stale; re-read the window AND the board the moment the
    // tab is looked at again. Refetch in place so returning to the tab does not
    // flash an empty board.
    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      void countDownToFinishNegotiation();
      refetchBoardInPlace();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    // After an RTA outage the socket reconnects but the events that fired while
    // it was down are gone. Re-read the window and drop the board cache.
    const unsubscribeReconnect = onRTAReconnect(() => {
      void countDownToFinishNegotiation();
      resyncBoard();
    });

    return () => {
      clearInterval(poll);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      unsubscribeReconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const countdown = marketCountdown(endNegotiation, getTime);
  const sealedClosing = Boolean(
    prepaidCycle &&
    notices.snapshot?.sealed &&
    hallPosts?.sealed &&
    hallPosts.cycleId === notices.snapshot.cycleId,
  );
  const sealedLeadingPositions = sealedClosing
    ? (hallPosts?.cards ?? [])
        .filter((card) => card.lastBidOrg === userInfoRef.current?.org_id)
        .map((card) => card.position)
        .sort((a, b) => a - b)
    : [];
  const sealedMinorNotice = sealedClosing
    ? notices.snapshot?.notices.find((notice) => notice.type === "minor_active")
    : undefined;

  // The transition the FE previously could not observe at all: the tick where a
  // known deadline runs out. Re-read the window immediately instead of waiting
  // up to MARKET_PERIOD_POLL_MS for the poll — but only ONCE per deadline.
  React.useEffect(() => {
    if (countdown.status !== "elapsed" || !endNegotiation) return;
    const deadline = endNegotiation.getTime();
    if (marketResyncedForDeadlineRef.current === deadline) return;
    marketResyncedForDeadlineRef.current = deadline;
    void countDownToFinishNegotiation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countdown.status, endNegotiation]);

  return (
    <NegotiationBalanceActionsContext.Provider value={balanceActions}>
      {isNegotiationModeReady() &&
        loadedModeRevision === modeRevision &&
        openConfig &&
        checkPermission(permissions.handleNegotiateConfigs, "view") && (
          <NegotiationConfig
            prepaid={prepaidCycle}
            value={
              prepaidCycle
                ? (orgStatus?.sponsorMinorAudience ?? false)
                : (orgStatus?.autoBuy ?? false)
            }
            disabled={!orgStatus || !checkPermission(permissions.handleNegotiateConfigs, "edit")}
            pending={autoBuyPending}
            error={autoBuyError}
            onChange={changeAutoBuyStatus}
            onClose={() => setOpenConfig(false)}
            onClearError={() => setAutoBuyError("")}
          />
        )}
      <FakeLoadingComponent loading={fakeLoading} />
      <div
        id="negotiationHomeContainer"
        style={{
          backgroundColor: turboMode ? "#060001" : "var(--bgColor)",
        }}
      >
        <Navbar selected="atrio" />
        <div className="tabPageContainer" id="hallPageContainer">
          {boardError && (
            <div className="hallBoardFailure">
              <BannerFeedback type="error" open message={boardError} />
              <button type="button" onClick={() => void getHallOfCards(true)}>
                Carregar publicações novamente
              </button>
            </div>
          )}
          {prepaidCycle && !marketPeriod && loadedModeRevision === modeRevision && (
            <section className="negotiationClosingSummary" aria-label="Fechamento da negociação">
              {sealedClosing ? (
                <>
                  <h2 className="title">Lances no fechamento</h2>
                  <p>Lideranças nas posições: {sealedLeadingPositions.join(", ") || "nenhuma"}.</p>
                  {sealedMinorNotice?.type === "minor_active" && (
                    <p>
                      Patrocínio do público menor nas posições:{" "}
                      {sealedMinorNotice.positions.join(", ")}. Seus lances, sem taxas: R${" "}
                      {formatBRL(sealedMinorNotice.totalCents / 100)}.
                    </p>
                  )}
                  <p>
                    Valor dos lances no fechamento: R$ {formatBRL(totalAmount())}. A apuração
                    confirma o consumo do saldo.
                  </p>
                </>
              ) : (
                <p role="status">
                  Fechando, lances em processamento. O resumo aparece quando o fechamento for
                  confirmado.
                </p>
              )}
            </section>
          )}
          {!isNegotiationModeReady() || loadedModeRevision !== modeRevision ? (
            <div id="hallMarketClosed" role="status">
              <span id="hallMarketClosedText">
                {getNegotiationModeError() ?? "Sincronizando o mercado..."}
              </span>
            </div>
          ) : !marketPeriod ? (
            <div id="hallMarketClosed">
              <div id="hallMarketClosedIcon">
                <LockRounded sx={{ fontSize: "120px" }} />
              </div>
              {/* "Abrindo o mercado..." is a claim about the cycle, so it is
                  reserved for a deadline this page actually watched run out.
                  With no usable or no fresh deadline the honest line is that the
                  window is being resynchronised. */}
              <span id="hallMarketClosedText">
                {countdown.status === "counting"
                  ? `O mercado está fechado`
                  : countdown.status === "elapsed"
                    ? "Abrindo o mercado..."
                    : "Sincronizando o mercado..."}
              </span>
              {countdown.status === "counting" && (
                <span id="hallMarketClosedSubtext">{displayRestantTime(endNegotiation)}</span>
              )}
            </div>
          ) : hallPosts?.cards ? (
            <>
              <BannerFeedback
                id="autoBuyWarningNegotiate"
                type="warning"
                message="A promoção automática está desativada! Sem ela, sua empresa não entra no pool de reserva caso o vencedor falhe. Ative nas configurações."
                open={!prepaidCycle && !orgStatus?.autoBuy}
              />
              <div className="hallGeneralResume" id="hallGeneralResume">
                {/* Two rows: the money (total, balance, settings) first, then
                    the clock and the offensive preview. */}
                <div className="hallGeneralResumeRow">
                  <div id="hallShowEstimative" className="hallEstimate">
                    <span className="hallEstimateLabel">
                      Total efetivo
                      <span className="hallEstimateValue">
                        {prepaidCycle && countdown.status !== "counting" && !sealedClosing
                          ? "Aguardando apuração"
                          : `R$ ${formatBRL(totalAmount())}`}
                      </span>
                    </span>
                    {(orgStatus?.bidDiscountPercent || 0) > 0 && (
                      <span className="hallEstimatePrize">
                        Lances brutos R$ {formatBRL(grossBidAmount())} ·{" "}
                        {orgStatus?.bidDiscountPercent}% OFF
                        {orgStatus?.monopolyFeeWaived ? " · sem taxa de monopólio" : ""}
                      </span>
                    )}
                  </div>
                  {balanceSlot && (
                    <>
                      <span style={{ color: "#999", fontSize: "30px" }}>•</span>
                      <div id="hallNegotiationBalance">{balanceSlot}</div>
                    </>
                  )}
                  {checkPermission(permissions.handleNegotiateConfigs, "view") && (
                    <>
                      <span style={{ color: "#999", fontSize: "30px" }}>•</span>
                      <button
                        type="button"
                        aria-label="Configurações da negociação"
                        className="negotiateConfigButton"
                        id="openNegotiateConfigIcon"
                        onClick={() => {
                          if (boardModeRevisionRef.current !== getNegotiationModeRevision()) return;
                          setOpenConfig(true);
                          void getOrgStats();
                        }}
                      >
                        <FontAwesomeIcon className="negotiateConfigIcon" icon={faCog} />
                      </button>
                    </>
                  )}
                </div>
                <div className="hallGeneralResumeRow">
                  <div
                    className="hallCountdownToFinishNegotiation"
                    id="hallCountdownToFinishNegotiation"
                  >
                    {/* Four states, not two. "Fechando o mercado..." is reserved
                        for a KNOWN deadline that JUST elapsed; an unknown /
                        rejected / stale deadline says so instead of asserting a
                        fact about the cycle that no field here proves. A deadline
                        that expired minutes ago falls through to `stale` and lands
                        on the same honest line as `unknown`, because a market that
                        has been "closing" for five minutes is not closing. */}
                    {countdown.status === "counting" ? (
                      <span>
                        <TimerRounded sx={{ fontSize: "40px", color: "#FFF" }} />{" "}
                        {displayRestantTime()}
                      </span>
                    ) : countdown.status === "elapsed" ? (
                      <span>
                        <PauseCircleFilled sx={{ fontSize: "40px", color: "#FFF" }} /> Fechando,
                        lances em processamento
                      </span>
                    ) : (
                      <span id="hallCountdownUnknown">
                        <HistoryToggleOffRounded sx={{ fontSize: "40px", color: "#FFF" }} />{" "}
                        Sincronizando o mercado...
                      </span>
                    )}
                  </div>
                  <span style={{ color: "#999", fontSize: "30px" }}>•</span>
                  {competitionExcluded ? (
                    <CompetitionExcludedMarker />
                  ) : (
                    <PreviewOffensivePoints
                      base={offensivePreview.base}
                      multiplier={offensivePreview.multiplier}
                    />
                  )}
                </div>
              </div>
              <div id="hallOptionsContainer">
                <div id="hallFilterButtonsContainer">
                  <div
                    onClick={() => setHallFilter("hits")}
                    className={hallFilter === "hits" ? "hallFilterHits" : "hallFilterButtonH"}
                  >
                    <TrendingUpRounded sx={{ fontSize: "36px" }} />
                    HITS
                  </div>
                  <div
                    onClick={() => setHallFilter("flops")}
                    className={hallFilter === "flops" ? "hallFilterFlops" : "hallFilterButtonF"}
                  >
                    <TrendingDownRounded sx={{ fontSize: "36px" }} />
                    FLOPS
                  </div>
                </div>
                <button
                  id="setTurboMode"
                  className={turboMode ? "turboModeActivated" : "turboModeDeactivated"}
                  onClick={() => canBid && supportsBidCreation() && setTurboMode(!turboMode)}
                  disabled={!canBid || !supportsBidCreation()}
                >
                  MODO TURBO
                  <SpeedOutlined sx={{ fontSize: "30px" }} />
                </button>
              </div>
              <div className="negotiationBidControls">
                <div className="amountControls">
                  <BidIncrementChips
                    selected={bidSelected}
                    onSelect={setBidSelected}
                    increments={bidIncrements}
                  />
                  {prepaidCycle && (
                    <NegotiationNotices
                      notices={notices.snapshot?.notices ?? []}
                      stale={notices.stale}
                      pending={notices.snapshot === null}
                    />
                  )}
                </div>
                {prepaidBalance && !checkPermission(permissions.orgPayments, "create") && (
                  <p className="negotiationBalanceHint">
                    Peça a alguém com permissão de Pagamentos para recarregar.
                  </p>
                )}
              </div>
              <HallOfCards
                key={`${modeRevision}:${periodWindowRef.current ?? "unknown"}`}
                bidWindowStart={bidWindowStartRef.current}
                hallFilter={hallFilter}
                cards={hallPosts?.cards || []}
                top1Id={hallPosts?.top1Id || ""}
                top2Id={hallPosts?.top2Id || ""}
                top3Id={hallPosts?.top3Id || ""}
                flop1Id={hallPosts?.flop1Id || ""}
                flop2Id={hallPosts?.flop2Id || ""}
                flop3Id={hallPosts?.flop3Id || ""}
                powerOffensiveIds={hallPosts?.powerOffensiveIds || []}
                selectedBid={bidSelected}
                turboMode={turboMode && canBid && supportsBidCreation()}
                onBidLanded={onBidLanded}
              />
            </>
          ) : (
            <div id="hallMarketClosed">
              <div id="hallMarketClosedIcon">
                <AcUnitRounded sx={{ fontSize: "120px" }} />
              </div>
              <span id="hallMarketClosedText">Sem posts para negociar.</span>
              {/* Same three-way read as the header above, for the same reason.
                  This line used to key off `displayRestantTime(...)` being a
                  non-empty string, and that helper returns "" for BOTH "there is
                  no deadline" and "the deadline passed", so an absent or stale
                  window was announced as a closing market and never recovered. */}
              <span id="hallMarketClosedSubtext">
                {countdown.status === "counting"
                  ? `Mercado fecha em ${displayRestantTime(endNegotiation)}`
                  : countdown.status === "elapsed"
                    ? "Fechando o mercado..."
                    : "Sincronizando o mercado..."}
              </span>
            </div>
          )}
        </div>
      </div>
      {recharge && prepaidBalance && (
        <RechargeFlow
          origin="auction"
          initialAmountBRL={recharge.amountBRL}
          balance={prepaidBalance}
          canAcceptTerms={checkPermission(permissions.orgPayments, "create")}
          onBalanceChanged={balanceActions.refresh}
          onClose={() => {
            setRecharge(null);
            balanceActions.refresh();
            // Closing after a credit re-reads every open preview, which
            // re-enables a bid the new balance now covers.
            balanceListenersRef.current.forEach((listener) => listener());
          }}
        />
      )}
    </NegotiationBalanceActionsContext.Provider>
  );
};

/**
 * `isCompetitionExcluded` for the Ranking and history tabs, which read no
 * org-status of their own. One read per negotiation mode: an answer read in
 * another mode (the tour's mock market, say) is never applied to this one.
 */
function useCompetitionExcluded(): boolean {
  const modeRevision = React.useSyncExternalStore(
    onNegotiationModeChange,
    getNegotiationModeRevision,
  );
  const { isDaKasaOrg = false } = useUser() ?? {};
  const [answer, setAnswer] = React.useState<{ revision: number; excluded?: boolean }>();

  React.useEffect(() => {
    const request = new AbortController();
    void (async () => {
      const JWT = (await localforage.getItem("JWT")) as string | undefined;
      if (!JWT || request.signal.aborted) return;
      try {
        const response = await negotiateAPI.get("/org-status", {
          headers: { "X-JWT": JWT },
          signal: request.signal,
          timeout: 10_000,
        });
        if (request.signal.aborted) return;
        const excluded = response.data?.competitionExcluded;
        setAnswer({
          revision: modeRevision,
          excluded: typeof excluded === "boolean" ? excluded : undefined,
        });
      } catch (error) {
        if (request.signal.aborted) return;
        console.error("Error fetching org status", error);
      }
    })();
    return () => request.abort();
  }, [modeRevision]);

  return isCompetitionExcluded(
    answer?.revision === modeRevision ? answer.excluded : undefined,
    isDaKasaOrg,
  );
}

interface orgRanking {
  offensivePoints: number;
  orgAvatar: string;
  orgName: string;
  rankingPosition: number;
  projectedBidDiscountPercent?: number;
  projectedMonopolyFeeWaived?: boolean;
}

function competitionCountdown(target: string | undefined, now: Date): string {
  if (!target) return "";
  const remaining = Math.max(0, new Date(target).getTime() - now.getTime());
  const totalSeconds = Math.floor(remaining / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const clock = [hours, minutes, seconds]
    .map((value) => value.toString().padStart(2, "0"))
    .join(":");
  return days > 0 ? `${days}d ${clock}` : clock;
}

const RankingPrize: React.FC<{ entry: orgRanking }> = ({ entry }) => {
  if (!entry.projectedBidDiscountPercent || !entry.projectedMonopolyFeeWaived) return null;
  return (
    <span className="rankingPrize">
      {entry.projectedBidDiscountPercent}% OFF · sem taxa de monopólio
    </span>
  );
};

export const Ranking: React.FC = () => {
  const [loading, setLoading] = React.useState<boolean>(true);
  const [rankingData, setRankingData] = React.useState<orgRanking[]>([]);
  const [hasError, setError] = React.useState<boolean>(false);
  const [competition, setCompetition] = React.useState<OffensiveCompetition>();
  const [clock, setClock] = React.useState<Date>(() => new Date());
  const competitionExcluded = useCompetitionExcluded();

  async function loadRanking(signal: AbortSignal) {
    const JWT = (await localforage.getItem("JWT")) as string | undefined;
    if (!JWT || signal.aborted) return false;

    const headers: { [key: string]: string | undefined } = {
      "X-JWT": JWT,
    };

    const totalRankingData: orgRanking[] = [];
    try {
      const competitionResponse = await negotiateAPI.get("/offensive-competition", {
        headers,
        signal,
        timeout: 10_000,
      });
      if (signal.aborted) return false;
      if (competitionResponse.data) setCompetition(competitionResponse.data);
    } catch (error) {
      if (signal.aborted) return false;
      console.error("Error fetching offensive competition", error);
    }
    for (let i = 0; true; i++) {
      if (signal.aborted) return false;
      try {
        const response = await negotiateAPI.get("/org-ranking", {
          params: { page: i },
          headers: headers,
          signal,
          timeout: 10_000,
        });
        if (signal.aborted) return false;
        const payload = response.data;
        if (!payload || (Array.isArray(payload) && payload.length === 0)) break;

        totalRankingData.push(...payload);
      } catch (error) {
        if (signal.aborted) return false;
        console.error("Error fetching org ranking", error);
        setLoading(false);
        setError(true);
        return false;
      }
    }

    const sortedRankingData = totalRankingData.sort(
      (a, b) => a.rankingPosition - b.rankingPosition,
    );
    setRankingData(sortedRankingData);
    setLoading(false);
  }

  React.useEffect(() => {
    const request = new AbortController();
    loadRanking(request.signal);
    const interval = window.setInterval(() => setClock(new Date()), 1000);
    return () => {
      request.abort();
      window.clearInterval(interval);
    };
  }, []);

  const competitionMessage =
    competition?.status === "reward_market"
      ? "Mercado premiado termina em"
      : competition?.status === "awaiting_reward_market"
        ? "Classificação encerrada · prêmio reservado para o próximo mercado com postagens"
        : "Evento termina em";

  return (
    <div id="rankingHomeContainer">
      <Navbar selected="ranking" />
      <div className="tabPageContainer" id="rankingPageContainer">
        {competition && (
          <section className="rankingCompetition" id="rankingCompetition">
            <div className="rankingCompetitionHeader">
              <div className="rankingCompetitionTitle">
                <EmojiEventsRounded />
                <div>
                  <span>Temporada de ofensiva</span>
                  <strong>{competitionMessage}</strong>
                </div>
              </div>
              {competition.status !== "awaiting_reward_market" && (
                <time className="rankingCompetitionTimer">
                  {competitionCountdown(competition.nextTransitionAt, clock)}
                </time>
              )}
            </div>
            <div className="rankingCompetitionPrizes">
              {competition.prizes.map((prize) => (
                <div className="rankingCompetitionPrize" key={prize.rank}>
                  <span>#{prize.rank}</span>
                  <strong>{prize.bidDiscountPercent}% OFF</strong>
                  <small>Sem taxa de monopólio no próximo mercado</small>
                </div>
              ))}
            </div>
            {competition.currentOrgReward && (
              <div className="rankingCompetitionReward">
                Sua empresa venceu em #{competition.currentOrgReward.rank}. Os lances continuam
                aparecendo pelo valor bruto; se você ganhar, cobrança e split usarão o total com
                {` ${competition.currentOrgReward.bidDiscountPercent}% OFF`}.
              </div>
            )}
          </section>
        )}
        {/* Ads already leaves the company out of the list (ADR 0018); this
            only says why it is not there. */}
        {competitionExcluded && (
          <p className="rankingExcludedNote" id="rankingExcludedNote">
            <InfoRounded aria-hidden="true" />
            <span>O DaKasa não participa do ranking nem dos prêmios do mês.</span>
          </p>
        )}
        {loading ? (
          <div id="negotiateGeneralLoadingContainer">
            <CircularProgress size={70} sx={{ color: "#FFF" }} />
            <h1>Carregando ranking...</h1>
          </div>
        ) : hasError ? (
          <div id="negotiateGeneralErrorContainer">
            <ErrorOutlineRounded sx={{ fontSize: "90px", color: "var(--danger-9)" }} />
            <h1>Erro ao carregar ranking. Tente novamente mais tarde</h1>
          </div>
        ) : rankingData.length === 0 ? (
          <div id="negotiateGeneralEmptyContainer">
            <EqualizerRounded sx={{ fontSize: "90px", color: "var(--neutral-9)" }} />
            <h1>Nenhuma empresa no ranking ainda</h1>
          </div>
        ) : (
          <div id="rankingContainer" className="ranking">
            <div id="rankingListTop3" className="rankingTop3">
              {rankingData.length > 1 && (
                <div className="rankingListTop3Item" id="rankingList2Place">
                  <span className="rankingListItemPositionTop3">#2</span>
                  <div className="rankingListItemOrgAvatarContainerTop3">
                    <img src={rankingData[1].orgAvatar} alt={rankingData[1].orgName} />
                  </div>
                  <span className="rankingListItemOrgNameTop3">{rankingData[1].orgName}</span>
                  <RankingPrize entry={rankingData[1]} />
                  <div className="rankingListItemOffensivePointsContainerTop3">
                    <span className="rankingListItemOffensivePointsTop3">
                      {rankingData[1].offensivePoints}
                    </span>
                    <FireComponent className="smallFire rankingFireTop3" />
                  </div>
                </div>
              )}

              {rankingData.length > 0 && (
                <div className="rankingListTop3Item" id="rankingList1Place">
                  <span className="rankingListItemPositionTop3">#1</span>
                  <div className="rankingListItemOrgAvatarContainerTop3">
                    <img src={rankingData[0].orgAvatar} alt={rankingData[0].orgName} />
                  </div>
                  <span className="rankingListItemOrgNameTop3">{rankingData[0].orgName}</span>
                  <RankingPrize entry={rankingData[0]} />
                  <div className="rankingListItemOffensivePointsContainerTop3">
                    <span className="rankingListItemOffensivePointsTop3">
                      {rankingData[0].offensivePoints}
                    </span>
                    <FireComponent className="smallFire rankingFireTop3" />
                  </div>
                </div>
              )}

              {rankingData.length > 2 && (
                <div className="rankingListTop3Item" id="rankingList3Place">
                  <span className="rankingListItemPositionTop3">#3</span>
                  <div className="rankingListItemOrgAvatarContainerTop3">
                    <img src={rankingData[2].orgAvatar} alt={rankingData[2].orgName} />
                  </div>
                  <span className="rankingListItemOrgNameTop3">{rankingData[2].orgName}</span>
                  <RankingPrize entry={rankingData[2]} />
                  <div className="rankingListItemOffensivePointsContainerTop3">
                    <span className="rankingListItemOffensivePointsTop3">
                      {rankingData[2].offensivePoints}
                    </span>
                    <FireComponent className="smallFire rankingFireTop3" />
                  </div>
                </div>
              )}
            </div>
            {rankingData.length > 3 && (
              <div id="rankingListContainer">
                <div className="rankingList">
                  {rankingData.slice(3).map((org) => (
                    <div key={org.rankingPosition} className="rankingListItem">
                      <span className="rankingListItemPosition">#{org.rankingPosition}</span>
                      <div className="rankingListItemOrgAvatarContainer">
                        <img src={org.orgAvatar} alt={org.orgName} />
                      </div>
                      <span className="rankingListItemOrgName">{org.orgName}</span>
                      <div className="rankingListItemOffensivePointsContainer">
                        <span className="rankingListItemOffensivePoints">
                          {org.offensivePoints}
                        </span>
                        <FireComponent className="smallFire rankingFire" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

interface negotiationHistory {
  winnerPostsPositions: number[];
  fallbackMinorPostsPositions: number[];
  offensiveAmountBefore: number;
  offensiveAmountAfter: number;
  powerMultiplier: number;
  cycleCost: number;
  taxCost: number;
  bidDiscountPercent?: number;
  monopolyFeeWaived?: boolean;
  grossCycleCost?: number;
  discountCost?: number;
  netCycleCost?: number;
  negotiatedAt: string;
}

const historyMonthFormat = new Intl.DateTimeFormat("pt-BR", { month: "long", year: "numeric" });
const historyDayFormat = new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "short" });
const historyWeekdayFormat = new Intl.DateTimeFormat("pt-BR", { weekday: "long" });

// Rows written before Ads migration 00010 carry net and gross as 0 (the
// columns were added without a backfill) and only the legacy bid and tax
// amounts.
function isLegacyHistory(item: negotiationHistory): boolean {
  return (
    !((item.netCycleCost ?? 0) > 0) &&
    !((item.grossCycleCost ?? 0) > 0) &&
    item.cycleCost + item.taxCost > 0
  );
}

function historyNetCost(item: negotiationHistory): number {
  if (isLegacyHistory(item)) return item.cycleCost + item.taxCost;
  return item.netCycleCost ?? item.cycleCost + item.taxCost;
}

// The cycle's ofensiva points: what the history row has always shown as
// before + (positions + 1) × (multiplier + 1). The record's after value is the
// absolute total, never added to the previous balance.
function historyOffensiveExplanation(item: negotiationHistory): string {
  const before = item.offensiveAmountBefore;
  const after = item.offensiveAmountAfter;
  const base =
    item.winnerPostsPositions.length + (item.fallbackMinorPostsPositions.length > 0 ? 1 : 0);
  const cycle = base + 1;
  const multiplier = item.powerMultiplier + 1;
  if (base > 0 && before + cycle * multiplier === after) {
    return item.powerMultiplier > 0
      ? `${before} antes + ${cycle} do ciclo × ${multiplier} de multiplicador = ${after}`
      : `${before} antes + ${cycle} do ciclo = ${after}`;
  }
  if (after < before) return `A ofensiva caiu de ${before} para ${after}.`;
  if (after === before) return `A ofensiva ficou em ${after}.`;
  return `A ofensiva foi de ${before} para ${after}.`;
}

const HistoryPositions: React.FC<{ item: negotiationHistory }> = ({ item }) => {
  const winners = [...item.winnerPostsPositions].sort((a, b) => a - b);
  const minors = [...item.fallbackMinorPostsPositions].sort((a, b) => a - b);
  if (winners.length === 0 && minors.length === 0)
    return <span className="negotiationHistoryMuted">Nenhuma posição neste ciclo</span>;
  return (
    <ul className="negotiationHistoryPositions" aria-label="Posições conquistadas">
      {winners.map((position) => (
        <li
          key={`winner-${position}`}
          className={`negotiationHistoryPosition negotiationHistoryPosition${
            position <= 3 ? position : "Other"
          }`}
        >
          #{position}
        </li>
      ))}
      {minors.map((position) => (
        <li
          key={`minor-${position}`}
          className="negotiationHistoryPosition negotiationHistoryPositionMinor"
        >
          Menores #{position}
        </li>
      ))}
    </ul>
  );
};

const HistoryCostDetails: React.FC<{ item: negotiationHistory }> = ({ item }) => {
  const net = historyNetCost(item);
  if (net <= 0) return <span className="negotiationHistoryMuted">Sem custo neste ciclo</span>;
  const rows: [string, string, string?][] = [];
  if (isLegacyHistory(item) || item.grossCycleCost === undefined) {
    rows.push(["Lances", `R$ ${formatBRL(item.cycleCost)}`]);
    rows.push(["Taxas", `R$ ${formatBRL(item.taxCost)}`]);
  } else {
    const gross = item.grossCycleCost;
    const discount = (item.bidDiscountPercent || 0) > 0 ? item.discountCost || 0 : 0;
    rows.push(["Lances do ciclo", `R$ ${formatBRL(gross)}`]);
    if (discount > 0)
      rows.push([
        `Prêmio de ${item.bidDiscountPercent}%`,
        `− R$ ${formatBRL(discount)}`,
        "negotiationHistoryPrize",
      ]);
    // The charge applies the organization's fee multiplier on top of the
    // bids, so the rows add up to the effective cost.
    const fees = net - (gross - discount);
    if (fees > 0.005) rows.push(["Taxas do ciclo", `R$ ${formatBRL(fees)}`]);
    else if (item.monopolyFeeWaived) rows.push(["Taxa de monopólio", "dispensada"]);
  }
  return (
    <dl className="negotiationHistoryCostDetails">
      {rows.map(([label, value, className]) => (
        <React.Fragment key={label}>
          <dt>{label}</dt>
          <dd className={className}>{value}</dd>
        </React.Fragment>
      ))}
      <dt className="negotiationHistoryCostTotal">Custo efetivo</dt>
      <dd className="negotiationHistoryCostTotal">R$ {formatBRL(net)}</dd>
    </dl>
  );
};

const HistoryRow: React.FC<{
  item: negotiationHistory;
  index: number;
  /** False for a company out of the offensive competition: no ofensiva cell or note. */
  showOffensive: boolean;
}> = ({ item, index, showOffensive }) => {
  const [open, setOpen] = React.useState(index === 0);
  const date = new Date(item.negotiatedAt);
  const day = historyDayFormat.format(date);
  const net = historyNetCost(item);
  const delta = item.offensiveAmountAfter - item.offensiveAmountBefore;
  const detailsId = `negotiationHistoryDetails${index}`;
  const discount = item.bidDiscountPercent || 0;
  return (
    <li className={`negotiationHistoryRowItem${open ? " negotiationHistoryRowOpen" : ""}`}>
      <div className="negotiationHistoryRow">
        <div className="negotiationHistoryDate">
          <span className="negotiationHistoryDay">{day}</span>
          <span className="negotiationHistoryMuted">{historyWeekdayFormat.format(date)}</span>
        </div>
        <HistoryPositions item={item} />
        {showOffensive && (
          <div
            className={`negotiationHistoryOffensive${delta < 0 ? " negotiationHistoryOffensiveDown" : ""}`}
          >
            <LocalFireDepartmentRounded className="negotiationHistoryFireIcon" aria-hidden="true" />
            <span className="negotiationHistoryOffensiveBefore">{item.offensiveAmountBefore}</span>
            <span aria-hidden="true">→</span>
            <span className="negotiationHistoryOffensiveAfter">{item.offensiveAmountAfter}</span>
            {delta !== 0 && (
              <span className="negotiationHistoryOffensiveDelta">
                {delta > 0 ? `+${delta}` : `−${Math.abs(delta)}`}
              </span>
            )}
          </div>
        )}
        <div className="negotiationHistoryCost">
          {net > 0 ? (
            <>
              <span className="negotiationHistoryCostValue">R$ {formatBRL(net)}</span>
              {discount > 0 ? (
                <span className="negotiationHistoryPrize">
                  {discount}% OFF{item.monopolyFeeWaived ? " · sem taxa de monopólio" : ""}
                </span>
              ) : isLegacyHistory(item) || item.netCycleCost === undefined ? (
                <span className="negotiationHistoryMuted">
                  R$ {formatBRL(item.cycleCost)} de lances + R$ {formatBRL(item.taxCost)} de taxas
                </span>
              ) : null}
            </>
          ) : (
            <span className="negotiationHistoryMuted">Sem custo</span>
          )}
        </div>
        <button
          type="button"
          className="negotiationHistoryToggle"
          aria-expanded={open}
          aria-controls={detailsId}
          aria-label={`${open ? "Recolher" : "Ver"} detalhes do ciclo de ${day}`}
          onClick={() => setOpen((value) => !value)}
        >
          <ExpandMoreRounded aria-hidden="true" />
        </button>
      </div>
      {open && (
        <div className="negotiationHistoryDetails" id={detailsId}>
          <HistoryCostDetails item={item} />
          {(showOffensive || item.fallbackMinorPostsPositions.length > 0) && (
            <div className="negotiationHistoryDetailsNotes">
              {showOffensive && <span>Ofensiva: {historyOffensiveExplanation(item)}</span>}
              {item.fallbackMinorPostsPositions.length > 0 && (
                <span>
                  {item.fallbackMinorPostsPositions.length === 1 ? "A posição" : "As posições"}{" "}
                  {item.fallbackMinorPostsPositions.map((position) => `#${position}`).join(", ")}{" "}
                  {item.fallbackMinorPostsPositions.length === 1 ? "foi ocupada" : "foram ocupadas"}{" "}
                  na audiência de menores.
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </li>
  );
};

export const NegotiateHistory: React.FC = () => {
  const [loading, setLoading] = React.useState<boolean>(true);
  const [hasError, setError] = React.useState<boolean>(false);
  const [negotiationData, setNegotiationData] = React.useState<negotiationHistory[]>([]);
  // Out of the competition, the ofensiva column, its notes and the summary card
  // go; the costs stay. Rows written before the exclusion keep their values.
  const competitionExcluded = useCompetitionExcluded();

  async function loadHistory(signal: AbortSignal) {
    const JWT = (await localforage.getItem("JWT")) as string | undefined;
    if (!JWT || signal.aborted) return false;

    const headers: { [key: string]: string | undefined } = {
      "X-JWT": JWT,
    };

    const now = new Date();
    const twoYearsAgo = new Date();
    twoYearsAgo.setFullYear(now.getFullYear() - 2);

    try {
      const response = await negotiateAPI.get("/cycle-history", {
        params: {
          startDate: twoYearsAgo.toISOString(),
          endDate: now.toISOString(),
        },
        headers: headers,
        signal,
        timeout: 10_000,
      });
      if (signal.aborted) return;
      const payload: negotiationHistory[] = response.data;
      if (!payload) return;

      const sortedHistoryData = payload.sort(
        (a, b) => new Date(b.negotiatedAt).getTime() - new Date(a.negotiatedAt).getTime(),
      );
      setNegotiationData(sortedHistoryData);
    } catch (error) {
      if (signal.aborted) return;
      console.error("Error fetching org history", error);
      setError(true);
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  }

  React.useEffect(() => {
    const request = new AbortController();
    loadHistory(request.signal);
    return () => request.abort();
  }, []);

  const months = React.useMemo(() => {
    const groups: {
      key: string;
      label: string;
      rows: { item: negotiationHistory; index: number }[];
    }[] = [];
    negotiationData.forEach((item, index) => {
      const date = new Date(item.negotiatedAt);
      const key = `${date.getFullYear()}-${date.getMonth()}`;
      let group = groups[groups.length - 1];
      if (!group || group.key !== key) {
        const label = historyMonthFormat.format(date);
        group = { key, label: label.charAt(0).toUpperCase() + label.slice(1), rows: [] };
        groups.push(group);
      }
      group.rows.push({ item, index });
    });
    return groups;
  }, [negotiationData]);
  const totalCost = negotiationData.reduce(
    (sum, item) => sum + Math.max(0, historyNetCost(item)),
    0,
  );
  // The monthly competition resets the ofensiva without a history row, so the
  // last row is the value after that cycle, not necessarily today's.
  const lastCycleOffensive = negotiationData[0]?.offensiveAmountAfter ?? 0;

  return (
    <div id="negotiationHistoryContainer">
      <Navbar selected="history" />
      {loading ? (
        <div id="negotiateGeneralLoadingContainer">
          <CircularProgress size={70} sx={{ color: "#FFF" }} />
          <h1>Carregando histórico...</h1>
        </div>
      ) : hasError ? (
        <div id="negotiateGeneralErrorContainer">
          <ErrorOutlineRounded sx={{ fontSize: "90px", color: "var(--danger-9)" }} />
          <h1>Erro ao carregar histórico. Tente novamente mais tarde</h1>
        </div>
      ) : (
        <div className="tabPageContainer" id="negotiationHistoryPageContainer">
          <div id="negotiateHistoryContainer">
            {negotiationData.length <= 0 ? (
              <div id="noNegotiationHistory">
                <HistoryToggleOffRounded id="noNegotiationHistoryIcon" />
                <span id="noNegotiationHistoryText">Organização ainda não possui histórico</span>
              </div>
            ) : (
              <div
                className={`negotiationHistoryContent${
                  competitionExcluded ? " negotiationHistoryWithoutOffensive" : ""
                }`}
              >
                <div className="negotiationHistoryHeading">
                  <h1>Histórico de leilões</h1>
                  <span>
                    {competitionExcluded
                      ? "Os ciclos da sua empresa nos últimos 2 anos"
                      : "Os ciclos que mudaram a ofensiva ou o custo da sua empresa nos últimos 2 anos"}
                  </span>
                </div>
                <div className="negotiationHistorySummary" id="negotiationHistorySummary">
                  <div className="negotiationHistoryStat">
                    <span>Ciclos registrados</span>
                    <strong>{negotiationData.length}</strong>
                  </div>
                  <div className="negotiationHistoryStat">
                    <span>Custo efetivo no período</span>
                    <strong>R$ {formatBRL(totalCost)}</strong>
                  </div>
                  {!competitionExcluded && (
                    <div className="negotiationHistoryStat negotiationHistoryStatFire">
                      <span>Ofensiva após o último ciclo</span>
                      <strong>{`${lastCycleOffensive} pontos`}</strong>
                    </div>
                  )}
                </div>
                <div className="negotiationHistoryTable" id="negotiationHistoryListContainer">
                  <div className="negotiationHistoryColumns" aria-hidden="true">
                    <span>Ciclo</span>
                    <span>Posições conquistadas</span>
                    {!competitionExcluded && <span>Ofensiva</span>}
                    <span className="negotiationHistoryColumnCost">Custo efetivo</span>
                    <span />
                  </div>
                  {months.map((month) => (
                    <section
                      key={month.key}
                      className="negotiationHistoryMonth"
                      aria-label={month.label}
                    >
                      <h2 className="negotiationHistoryMonthTitle">{month.label}</h2>
                      <ul className="negotiationHistoryRows">
                        {month.rows.map(({ item, index }) => (
                          <HistoryRow
                            key={`${item.negotiatedAt}-${index}`}
                            item={item}
                            index={index}
                            showOffensive={!competitionExcluded}
                          />
                        ))}
                      </ul>
                    </section>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const Negotiate: React.FC = () => {
  React.useEffect(() => {
    RTA.connect();
  }, []);

  // Root owns both providers for every negotiation route. A second UserProvider
  // has no selected tutorial and would disable the shared mock/session while
  // Root's tour is still active.
  return <NegotiateHome />;
};
